import { setPageContext } from "../utils/pageContext.js";
import { renderPageHeader } from "../ui/pageHeader.js";

import { toast } from "../ui/toast.js";
import { confirmModal } from "../ui/confirmModal.js";
import { toDatetimeLocalValue } from "../utils/date.js";

function apRequest(url, body, method) {
    const m = method || (body != null ? 'POST' : 'GET');
    const opts = { method: m, headers: { 'Content-Type': 'application/json' }, credentials: 'include' };
    if (body != null && m !== 'GET') opts.body = JSON.stringify(body);
    // CSRF token (same pattern as the rest of the app)
    try {
        const t = document.cookie.split('; ').find(x => x.startsWith('csrf_token='));
        if (t) opts.headers['X-CSRF-Token'] = decodeURIComponent(t.split('=')[1]);
    } catch (_) {}
    return fetch(url, opts).then(async r => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error?.message || j.error || `HTTP ${r.status}`);
        return j;
    });
}



/**
 * Autopilot dashboard — live data, Phase 1 Commit 12.
 *
 * Pulls state from /api/autopilot/state and renders:
 *   - Status ribbon (on / paused / off)
 *   - Preset picker (live — turns autopilot on with defaults)
 *   - Next post card (chosen topic, drafted topic, or proposed list)
 *   - Topic queue / veto pick UI
 *   - Quick actions: Pause, Resume, Seed now
 *   - Deep-link to Brain chat with autopilot context
 *
 * Not in this commit: advanced settings modal, topic swap UI, last-month
 * reflection card (wires once we have reflections stored — Phase 4).
 */
// Preset labels shown in the ribbon + picker. Keys match DB preset values.
// Mirror of server/lib/topicPlanner.js WEIGHTS. The topic-detail API
// also returns the authoritative weights — we use those when available
// and fall back to this mirror for the list-card running-total calc.
const SCORE_WEIGHTS = {
    source:      0.18,
    audience:    0.22,
    coverage:    0.18,
    recency:     0.10,
    performance: 0.12,
    quality:     0.20,
};

const PRESET_LABELS = {
    manual_review: { label: 'Manual review', oneLiner: 'you approve every blog and every social promo' },
    assisted:      { label: 'Assisted',      oneLiner: 'you approve the blog, promos auto-fire' },
    autonomous:    { label: 'Autonomous',    oneLiner: 'nothing waits for you' },
    // Legacy fallbacks for brands that haven't had their preset renamed
    cautious:  { label: 'Manual review', oneLiner: 'you approve every blog and every social promo' },
    balanced:  { label: 'Assisted',      oneLiner: 'you approve the blog, promos auto-fire' },
    full_auto: { label: 'Autonomous',    oneLiner: 'nothing waits for you' },
};

// Full rule-set shown in the "what this means" expanders on the picker
const PRESET_RULES = {
    manual_review: [
        'Blog: waits for your approval before publishing',
        'Social promos: waits for your approval per platform',
        'Cadence: weekly, Mon 9am local',
        'Notifications: in-app + email',
        'Faceless video: off by default (opt in per stream)',
    ],
    assisted: [
        'Blog: waits for your approval (usually Mon morning)',
        'Social promos: fire automatically once you approve the blog',
        'Cadence: weekly, Mon 7am local',
        'Notifications: in-app + email',
        'Faceless video: on by default (one per weekly cycle)',
    ],
    autonomous: [
        'Blog: publishes automatically on schedule',
        'Social promos: auto-fire after blog publishes',
        'Cadence: weekly, Mon 7am local',
        'Notifications: in-app + email (for visibility, not approval)',
        'Faceless video: on by default (one per weekly cycle)',
    ],
};

// Phase 37 Commit 9 — default `config.include_video` per preset. Stream
// creation in autopilotStreams.js applies these when the user picks a
// preset; users can still override the toggle per stream.
const PRESET_INCLUDE_VIDEO_DEFAULTS = {
    manual_review: false,
    assisted:      true,
    autonomous:    true,
};

export class AutopilotPage {
    constructor() {
        this.el = null;
        this.state = { loading: true, error: null, data: null, brandId: null, settingsOpen: false, seeding: false, deletingTopicIds: new Set(), activeStreamId: null, newStreamOpen: false, creatingStream: false, pauseMenuOpen: false, topicPickerOpenId: null, chipPopover: null, editedTopicId: null, showEditedBanner: false, reflectionOpen: false, sourcesHelpOpen: false, newStreamType: 'blog_cycle' };
    }

    async mount(el) {
        this.el = el;
        setPageContext({ page: "autopilot", brandId: this._currentBrandId ? undefined : null });
        this.brandId = this._currentBrandId();
        this.advanced = this._isAdvancedMode();
        this._installLiveRefresh();
        if (!this.advanced) {
            // Phase 31: simple-mode is the default. Renders an entirely
            // separate UI from the advanced stream-config page.
            await this._mountSimple();
            return;
        }
        this._render();
        await this._load();
        // Handle email deep-link actions like #autopilot?pick=topic_xxx
        this._handleHashActions();
    }

    // ─── Live dashboard refresh (Branch 1 Group B) ────────────────────────
    // Refetch on tab refocus + when content changes elsewhere, so the page
    // never shows stale autopilot state. Mirrors analytics.js. All listeners
    // + the interval are torn down in unmount() (called by the router).
    _installLiveRefresh() {
        // Refetch on tab focus (user switched away, came back — data is stale).
        this._onVisibility = () => {
            if (document.visibilityState === 'visible') this._refresh();
        };
        document.addEventListener('visibilitychange', this._onVisibility);

        // Refetch when content mutates anywhere else in the app (debounced).
        this._onContentChanged = () => {
            clearTimeout(this._refreshTimer);
            this._refreshTimer = setTimeout(() => this._refresh(), 350);
        };
        window.addEventListener('heist:content-changed', this._onContentChanged);

        // Light polling while the tab is visible — refetch-on-focus is the
        // primary win; this catches server-side changes on an idle open tab.
        this._refreshInterval = setInterval(() => {
            if (document.visibilityState === 'visible') this._refresh();
        }, 60000);
    }

    // Re-run the mode-appropriate loader and re-render into the existing
    // container (no full remount flash). Skips while a modal overlay is open
    // so we don't yank a confirm / custom-pause dialog out from under the user.
    async _refresh() {
        if (!this.el) return;
        // Never re-render out from under an open dialog or in-progress edit.
        if (document.querySelector('.g-modal-overlay, .ap-modal-overlay')) return;
        if (this.advanced && this.state && (
            this.state.settingsOpen || this.state.newStreamOpen ||
            this.state.chipPopover || this.state.topicPickerOpenId ||
            this.state.pauseMenuOpen || this.state.seeding || this.state.creatingStream
        )) return;
        try {
            if (this.advanced) {
                await this._load();
            } else if (this.simple) {
                await this._loadSimple();
            }
        } catch (_) { /* transient fetch errors are surfaced by the loaders */ }
    }

    async unmount() {
        if (this._onVisibility) {
            document.removeEventListener('visibilitychange', this._onVisibility);
            this._onVisibility = null;
        }
        if (this._onContentChanged) {
            window.removeEventListener('heist:content-changed', this._onContentChanged);
            this._onContentChanged = null;
        }
        clearTimeout(this._refreshTimer);
        if (this._refreshInterval) { clearInterval(this._refreshInterval); this._refreshInterval = null; }
        this.el = null;
    }

    _isAdvancedMode() {
        try {
            const hash = window.location.hash || '';
            const q = hash.split('?')[1];
            if (!q) return false;
            return new URLSearchParams(q).get('advanced') === '1';
        } catch (_) { return false; }
    }

    // ─── Phase 31 simple-mode UI ──────────────────────────────────────────
    //
    // Self-contained mount/render/listeners for the one-switch page. Reads
    // /api/autopilot/simple/* and never touches the existing _load /
    // _render path (which still serves the advanced ?advanced=1 view).

    async _mountSimple() {
        this.simple = { state: null, loading: true, error: null };
        this._renderSimple();
        await this._loadSimple();
    }

    async _loadSimple() {
        if (!this.brandId) {
            this.simple.loading = false;
            this.simple.error = 'Pick a brand to set up autopilot.';
            this._renderSimple();
            return;
        }
        try {
            const state = await apRequest(
                `/api/autopilot/simple/state?brandId=${encodeURIComponent(this.brandId)}`
            );
            this.simple.state = state;
            this.simple.error = null;
        } catch (e) {
            this.simple.error = e.message || 'Failed to load autopilot state';
        } finally {
            this.simple.loading = false;
        }
        this._renderSimple();
    }

    _simpleStatusLine(state) {
        if (!state.enabled) return 'off';
        if (state.pausedUntil) {
            const ms = new Date(state.pausedUntil).getTime() - Date.now();
            if (ms > 0) {
                const hours = Math.round(ms / (60 * 60 * 1000));
                if (hours < 24) return `paused — resumes in ${hours}h`;
                const days = Math.round(hours / 24);
                return `paused — resumes in ${days} day${days === 1 ? '' : 's'}`;
            }
        }
        if (state.startedAt) {
            const since = new Date(state.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `on — running since ${since}`;
        }
        return 'on';
    }

    _simpleRelativeTime(iso) {
        if (!iso) return '';
        const ms = Date.now() - new Date(iso).getTime();
        const ago = ms >= 0;
        const abs = Math.abs(ms);
        const mins = Math.round(abs / 60000);
        if (mins < 1) return ago ? 'just now' : 'in <1 min';
        if (mins < 60) return ago ? `${mins}m ago` : `in ${mins}m`;
        const hours = Math.round(mins / 60);
        if (hours < 24) return ago ? `${hours}h ago` : `in ${hours}h`;
        const days = Math.round(hours / 24);
        return ago ? `${days}d ago` : `in ${days}d`;
    }

    _simpleEscape(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    _renderSimple() {
        if (!this.el) return;
        const header = renderPageHeader({
            title: 'Autopilot',
            subtitle: "Heist runs your content automatically using your brain's strategy.",
        });

        if (this.simple.loading) {
            this.el.innerHTML = `<div class="ap-scope">${header}</div><div class="ap-simple-page"><div class="ap-loading">Loading…</div></div>`;
            return;
        }

        if (this.simple.error || !this.simple.state) {
            this.el.innerHTML = `<div class="ap-scope">${header}</div>
              <div class="ap-simple-page">
                <div class="ap-simple-doing-box">${this._simpleEscape(this.simple.error || 'No data')}</div>
                <div class="ap-simple-advanced-link"><a href="#autopilot?advanced=1">advanced mode: streams, schedule &amp; approvals</a></div>
              </div>`;
            return;
        }

        const state = this.simple.state;
        const enabled = !!state.enabled;
        const isPaused = enabled && state.pausedUntil && new Date(state.pausedUntil).getTime() > Date.now();
        const lastRel = this._simpleRelativeTime(state.lastPost?.publishedAt);
        const nextRel = this._simpleRelativeTime(state.nextPost?.scheduledAt);
        const weekCount = state.thisWeek?.count ?? 0;
        const weekPlatforms = state.thisWeek?.platformsCount ?? 0;

        const needsStrategyBanner = state.needsStrategy
            ? `<div class="ap-simple-needs-strategy-banner">
                 Autopilot is on but has no strategy yet —
                 <a href="#brain">add a strategy in your brain</a> so it knows what to write.
               </div>`
            : '';

        this.el.innerHTML = `
            <div class="ap-scope">${header}</div>
            <div class="ap-simple-page">

              <div class="ap-simple-announce">
                <div class="ap-simple-announce-title">Two ways to run autopilot</div>
                <div class="ap-simple-announce-body">
                  <strong>1. Plug in an AI key</strong> in <a href="#settings?tab=ai">Settings &rarr; AI Settings</a>, leave the toggle below <em>on</em>. Heist checks in every 15 minutes and handles everything. Free option: use OpenRouter's free models.
                  <br><br>
                  <strong>2. Run it from your AI chat</strong> (Claude.ai or ChatGPT). Set up a routine that connects to Heist's MCP and creates drafts twice a day &mdash; uses your existing chat subscription, no Heist AI cost. <strong>If you go this route, turn the toggle below off</strong> so Heist doesn't generate the same content twice.
                </div>
                <a class="ap-simple-announce-link" href="#help?tab=guides&guide=autopilot-overview">Learn more &rarr;</a>
              </div>

              <label class="ap-simple-toggle-row" data-action="toggle">
                <div>
                  <div class="ap-simple-toggle-title">run my content automatically</div>
                  <div class="ap-simple-toggle-status" data-testid="ap-simple-status" aria-live="polite">${this._simpleEscape(this._simpleStatusLine(state))}</div>
                </div>
                <span class="ap-simple-switch ${enabled ? 'on' : ''}" data-testid="ap-simple-switch">
                  <input type="checkbox" class="ap-simple-switch-input" data-action="toggle-input" ${enabled ? 'checked' : ''} aria-label="Run my content automatically">
                  <span class="ap-simple-switch-knob"></span>
                </span>
              </label>

              ${needsStrategyBanner}

              <div class="ap-simple-stat-row">
                <div class="ap-simple-stat">
                  <div class="ap-simple-stat-label">last post</div>
                  <div class="ap-simple-stat-value">${this._simpleEscape(lastRel || '—')}</div>
                  <div class="ap-simple-stat-platform">${this._simpleEscape(state.lastPost?.platform || '')}</div>
                </div>
                <div class="ap-simple-stat">
                  <div class="ap-simple-stat-label">next post</div>
                  <div class="ap-simple-stat-value">${this._simpleEscape(nextRel || '—')}</div>
                  <div class="ap-simple-stat-platform">${this._simpleEscape(state.nextPost?.platform || '')}</div>
                </div>
                <div class="ap-simple-stat">
                  <div class="ap-simple-stat-label">this week</div>
                  <div class="ap-simple-stat-value">${weekCount} post${weekCount === 1 ? '' : 's'}</div>
                  <div class="ap-simple-stat-platform">${weekPlatforms} platform${weekPlatforms === 1 ? '' : 's'}</div>
                </div>
              </div>

              <div class="ap-simple-doing-pause-row">
                <div class="ap-simple-doing-box">
                  <div class="ap-simple-doing-title">what autopilot is doing right now</div>
                  <ul class="ap-simple-doing-list">
                    ${(state.doingNow || []).map(s => `<li>${this._simpleEscape(s)}</li>`).join('') || '<li>nothing yet — turn autopilot on to get started</li>'}
                  </ul>
                </div>

                <div class="ap-simple-pause-section ${enabled ? '' : 'is-disabled'}">
                  <div class="ap-simple-pause-label">need a break?</div>
                  <div class="ap-simple-pause-row">
                    <button class="ap-simple-pause-btn" data-action="pause" data-duration="today" ${enabled ? '' : 'disabled'}>rest of today</button>
                    <button class="ap-simple-pause-btn" data-action="pause" data-duration="3days" ${enabled ? '' : 'disabled'}>3 days</button>
                    <button class="ap-simple-pause-btn" data-action="pause" data-duration="1week" ${enabled ? '' : 'disabled'}>1 week</button>
                    <button class="ap-simple-pause-btn" data-action="pause" data-duration="custom" ${enabled ? '' : 'disabled'}>custom</button>
                  </div>
                  ${isPaused ? `<button class="ap-simple-pause-btn ap-simple-resume-btn" data-action="resume-now" data-testid="ap-simple-resume">resume now</button>` : ''}
                </div>
              </div>

              <button class="ap-simple-secondary-btn" data-action="view-week">view this week's posts</button>

              <div class="ap-simple-advanced-link">
                <a href="#autopilot?advanced=1">advanced mode: streams, schedule &amp; approvals</a>
              </div>
            </div>
        `;

        this._attachSimpleListeners();
    }

    _attachSimpleListeners() {
        if (!this.el) return;
        // Toggle — native checkbox in a <label> gives us role="switch"
        // semantics, focus, and Space/Enter keyboard operation for free.
        // We listen for `change` on the input only (a wrapping <label> click
        // toggles the checkbox natively, so a separate row click handler would
        // double-fire).
        const toggleInput = this.el.querySelector('.ap-simple-switch-input');
        if (toggleInput) {
            toggleInput.addEventListener('change', () => this._simpleToggle());
        }
        // Pause buttons (skip disabled ones — clicking them is a no-op anyway)
        this.el.querySelectorAll('[data-action="pause"]').forEach(btn => {
            btn.addEventListener('click', (ev) => {
                if (btn.disabled) return;
                const duration = btn.getAttribute('data-duration');
                this._simplePause(duration);
            });
        });
        // Resume now (only rendered while paused)
        const resume = this.el.querySelector('[data-action="resume-now"]');
        if (resume) resume.addEventListener('click', () => this._simpleResume());
        // View week → library filtered to last 7d
        const view = this.el.querySelector('[data-action="view-week"]');
        if (view) view.addEventListener('click', () => {
            window.location.hash = '#library?range=7d';
        });
    }

    async _simpleToggle() {
        if (!this.brandId || !this.simple.state) return;
        const next = !this.simple.state.enabled;
        try {
            const r = await apRequest('/api/autopilot/simple/toggle', { brandId: this.brandId, enabled: next });
            this.simple.state = r.state || this.simple.state;
            toast('success', next ? 'Autopilot on.' : 'Autopilot off.');
            this._renderSimple();
        } catch (e) {
            toast('error', e.message);
        }
    }

    async _simplePause(duration) {
        if (!this.brandId || !duration) return;
        let body;
        if (duration === 'custom') {
            // Real datetime-local picker (replaces the old window.prompt).
            const raw = await this._promptCustomPause();
            if (!raw) return; // cancelled
            const when = new Date(raw);
            if (isNaN(when.getTime()) || when.getTime() <= Date.now()) {
                toast('error', 'Custom date must parse and be in the future.');
                return;
            }
            body = { brandId: this.brandId, duration: { custom: when.toISOString() } };
        } else {
            body = { brandId: this.brandId, duration };
        }
        try {
            const r = await apRequest('/api/autopilot/simple/pause', body);
            this.simple.state = r.state || this.simple.state;
            toast('success', 'Autopilot paused.');
            this._renderSimple();
        } catch (e) {
            toast('error', e.message);
        }
    }

    // Custom-pause date picker. Resolves to the raw datetime-local string
    // (e.g. "2026-07-14T09:00") on confirm, or null on cancel. Reuses the
    // global g-modal shell (styled in styles.css, available in simple mode)
    // + toDatetimeLocalValue from utils/date.js. Default = now + 3 days.
    _promptCustomPause() {
        return new Promise((resolve) => {
            let settled = false;
            const cleanup = (result) => {
                if (settled) return;
                settled = true;
                document.removeEventListener('keydown', onKey);
                overlay.remove();
                resolve(result);
            };

            const overlay = document.createElement('div');
            overlay.className = 'g-modal-overlay';
            overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(null); });

            const dialog = document.createElement('div');
            dialog.className = 'g-modal';
            dialog.setAttribute('role', 'dialog');
            dialog.setAttribute('aria-modal', 'true');
            dialog.setAttribute('aria-labelledby', 'ap-custom-pause-title');

            const titleEl = document.createElement('h3');
            titleEl.id = 'ap-custom-pause-title';
            titleEl.className = 'g-modal__title';
            titleEl.textContent = 'Pause autopilot until…';

            const msgEl = document.createElement('p');
            msgEl.className = 'g-modal__msg';
            msgEl.textContent = 'Autopilot will stay off until this date and time, then resume automatically.';

            const now = new Date();
            const input = document.createElement('input');
            input.type = 'datetime-local';
            input.className = 'g-modal__input';
            input.setAttribute('data-testid', 'ap-custom-pause-input');
            input.min = toDatetimeLocalValue(now);
            input.value = toDatetimeLocalValue(new Date(now.getTime() + 3 * 86400000));

            const footer = document.createElement('div');
            footer.className = 'g-modal__footer';

            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.className = 'g-modal__btn g-modal__btn--cancel';
            cancelBtn.textContent = 'Cancel';
            cancelBtn.onclick = () => cleanup(null);

            const confirmBtn = document.createElement('button');
            confirmBtn.type = 'button';
            confirmBtn.className = 'g-modal__btn g-modal__btn--confirm';
            confirmBtn.textContent = 'Pause';
            confirmBtn.onclick = () => cleanup(input.value || null);

            footer.appendChild(cancelBtn);
            footer.appendChild(confirmBtn);
            dialog.appendChild(titleEl);
            dialog.appendChild(msgEl);
            dialog.appendChild(input);
            dialog.appendChild(footer);
            overlay.appendChild(dialog);

            const onKey = (e) => {
                if (e.key === 'Escape') cleanup(null);
                if (e.key === 'Enter' && document.activeElement === input) cleanup(input.value || null);
            };
            document.addEventListener('keydown', onKey);
            document.body.appendChild(overlay);
            requestAnimationFrame(() => input.focus());
        });
    }

    async _simpleResume() {
        if (!this.brandId) return;
        try {
            const r = await apRequest('/api/autopilot/simple/resume', { brandId: this.brandId });
            this.simple.state = r.state || this.simple.state;
            toast('success', 'Autopilot resumed.');
            this._renderSimple();
        } catch (e) {
            toast('error', e.message);
        }
    }
    // ─── End Phase 31 simple-mode UI ──────────────────────────────────────

    async _handleHashActions() {
        const hash = window.location.hash || '';
        const q = hash.split('?')[1];
        if (!q) return;
        const params = new URLSearchParams(q);
        const pick = params.get('pick');
        if (pick && this.brandId) {
            try {
                await apRequest('/api/autopilot/pick-topic', { brandId: this.brandId, topicId: pick });
                toast('success', 'Topic picked — the Brain will draft your blog automatically.');
                window.location.hash = '#autopilot';
                await this._load();
            } catch (e) {
                toast('error', 'Could not pick that topic: ' + (e.message || 'unknown'));
            }
        }
    }

    _currentBrandId() {
        // Pull from window.__activeBrand__ / localStorage fallback
        try {
            if (window.__activeBrand__?.id) return window.__activeBrand__.id;
            const b = localStorage.getItem('heist_active_brand');
            if (b) return b;
        } catch (_) {}
        return null;
    }

    async _load() {
        if (!this.brandId) {
            this.state.loading = false;
            this.state.error = 'Pick a brand to configure autopilot.';
            this._render();
            return;
        }
        try {
            const [state] = await Promise.all([
                apRequest(`/api/autopilot/state?brandId=${encodeURIComponent(this.brandId)}`),
                this._loadCustomSources(),
            ]);
            this.state.loading = false;
            this.state.data = state;
            this.state.error = null;
            // Pass 3: ?topic=X&edited=1 deep-link (land back from chat edit)
            try {
                const hash = (location.hash || '').replace(/^#[^?]*\??/, '');
                const qs = new URLSearchParams(hash);
                const deepTopic = qs.get('topic');
                const edited = qs.get('edited');
                if (deepTopic && edited === '1') {
                    this.state.editedTopicId = deepTopic;
                    this.state.showEditedBanner = true;
                    // Clear the query portion so a reload doesn't repeat the banner
                    const base = (location.hash || '').split('?')[0];
                    history.replaceState(null, '', location.pathname + base);
                }
            } catch (_) {}
            // Select default stream if nothing selected yet (or the selection vanished)
            const streams = Array.isArray(state?.streams) ? state.streams : [];
            if (streams.length) {
                const stillExists = this.state.activeStreamId && streams.some(x => x.id === this.state.activeStreamId);
                if (!stillExists) {
                    const def = streams.find(x => x.isDefault) || streams[0];
                    this.state.activeStreamId = def.id;
                }
            } else {
                this.state.activeStreamId = null;
            }
        } catch (e) {
            this.state.loading = false;
            this.state.error = e.message || 'Failed to load autopilot state';
        }
        this._render();
    }

    async _enable(preset) {
        try {
            await apRequest('/api/autopilot/enable', { brandId: this.brandId, preset });
            toast('success', `Autopilot turned on (${preset})`);
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _pause() {
        if (!await confirmModal({ title: 'Pause autopilot', message: 'Pause autopilot for 7 days?', confirmLabel: 'Pause', danger: true })) return;
        try {
            await apRequest('/api/autopilot/pause', { brandId: this.brandId });
            toast('success', 'Paused');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _pauseFor(days) {
        try {
            const until = new Date(Date.now() + Math.max(1, days) * 86400000).toISOString();
            await apRequest('/api/autopilot/pause', { brandId: this.brandId, pausedUntil: until });
            toast('success', days >= 9999 ? 'Paused indefinitely' : ('Paused for ' + days + ' days'));
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    // Collect every [data-apset] field in the advanced settings modal and
    // persist in one PATCH. Checkbox groups (blog_destinations /
    // social_platforms) accumulate into arrays; single checkboxes → 1|0.
    async _saveSettings() {
        const fields = this.el ? this.el.querySelectorAll('.ap-modal [data-apset]') : [];
        if (!fields.length) return;
        const patch = { blog_destinations: [], social_platforms: [] };
        fields.forEach(f => {
            const key = f.getAttribute('data-apset');
            if (key === 'blog_destinations' || key === 'social_platforms') {
                if (f.checked) patch[key].push(f.getAttribute('data-val'));
            } else if (f.type === 'checkbox') {
                patch[key] = f.checked ? 1 : 0;
            } else if (key === 'publish_hour' || key === 'topic_veto_hours_before') {
                patch[key] = Number(f.value);
            } else {
                patch[key] = f.value;
            }
        });
        try {
            await apRequest('/api/autopilot/settings', Object.assign({ brandId: this.brandId }, patch));
            this.state.settingsOpen = false;
            toast('success', 'Settings saved');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _saveChipSettings(patch) {
        try {
            await apRequest('/api/autopilot/settings', Object.assign({ brandId: this.brandId }, patch));
            toast('success', 'Saved');
            this.state.chipPopover = null;
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _resume() {
        try {
            await apRequest('/api/autopilot/resume', { brandId: this.brandId });
            toast('success', 'Resumed');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _chatAboutTopic(topicId, topicTitle) {
        if (!topicId) return;
        try {
            const r = await apRequest('/api/chat/threads/pinned', {
                resourceType: 'topic',
                resourceId: topicId,
                title: topicTitle ? `About: ${topicTitle.slice(0, 60)}` : 'About topic',
            });
            if (!r || !r.id) throw new Error('Could not open chat thread');
            try {
                sessionStorage.setItem('brain.activeTab', 'chat');
                localStorage.setItem('brain.activeThreadId', r.id);
            } catch(_) {}
            window.location.hash = `#memory?thread=${encodeURIComponent(r.id)}`;
        } catch (e) {
            toast('error', e.message || 'Could not open chat');
        }
    }

    async _pickTopic(topicId) {
        try {
            await apRequest('/api/autopilot/pick-topic', { brandId: this.brandId, topicId });
            toast('success', 'Topic picked');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _seedNow() {
        if (this.state.seeding) return; // guard double-click
        this.state.seeding = true;
        this._render();
        try {
            const r = await apRequest('/api/autopilot/seed-now', { brandId: this.brandId });
            toast('success', `Seeded ${r.top5?.length || 0} topic candidates`);
            await this._load(); // _load resets loading state, but not seeding
        } catch (e) {
            toast('error', e.message);
        } finally {
            this.state.seeding = false;
            this._render();
        }
    }

    async _sendVetoNow() {
        try {
            const r = await apRequest('/api/autopilot/send-veto-now', { brandId: this.brandId });
            if (r && r.sent) {
                toast('success', 'Veto notification sent.');
            } else if (r && r.stub) {
                toast('info', 'Veto is a no-op right now (stub).');
            } else {
                toast('info', r && r.reason ? `Veto not sent: ${r.reason}` : 'Veto not sent.');
            }
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    _selectStream(streamId) {
        if (!streamId || streamId === this.state.activeStreamId) return;
        this.state.activeStreamId = streamId;
        this._render();
    }

    async _openNewStream() {
        this.state.newStreamOpen = true;
        this._render();
    }

    async _closeNewStream() {
        this.state.newStreamOpen = false;
        this._render();
    }

    async _createStream(name, type, configPatch) {
        if (this.state.creatingStream) return;
        this.state.creatingStream = true;
        this._render();
        try {
            const baseConfig = { blog_destinations: ['website'], social_platforms: [] };
            const r = await apRequest('/api/autopilot/streams', {
                brandId: this.brandId,
                name: name || 'New stream',
                type: type || 'blog_cycle',
                preset: 'assisted',
                enabled: true,
                schedule: { publish_day: 'monday', publish_hour: 7, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles' },
                approval: { topic_veto_hours_before: 14, auto_default_on_no_response: false, blog_approval_required: true },
                config: { ...baseConfig, ...(configPatch || {}) },
            });
            toast('success', 'Stream created');
            this.state.newStreamOpen = false;
            this.state.activeStreamId = r?.stream?.id || this.state.activeStreamId;
            await this._load();
        } catch (e) {
            toast('error', e.message);
        } finally {
            this.state.creatingStream = false;
            this._render();
        }
    }

    async _pauseStream(streamId) {
        try {
            const until = new Date(Date.now() + 7 * 86400000).toISOString();
            await apRequest(`/api/autopilot/streams/${encodeURIComponent(streamId)}/pause`, { until });
            toast('success', 'Stream paused for 7 days');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _resumeStream(streamId) {
        try {
            await apRequest(`/api/autopilot/streams/${encodeURIComponent(streamId)}/resume`, {});
            toast('success', 'Stream resumed');
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _deleteStream(streamId) {
        const stream = (this.state.data?.streams || []).find(x => x.id === streamId);
        if (!stream) return;
        if (stream.isDefault) {
            toast('error', 'Default stream cannot be deleted');
            return;
        }
        if (!await confirmModal({ title: 'Delete stream', message: `Delete stream "${stream.name}"? This cannot be undone.`, confirmLabel: 'Delete', danger: true })) return;
        try {
            await apRequest(`/api/autopilot/streams/${encodeURIComponent(streamId)}`, null, 'DELETE');
            toast('success', 'Stream deleted');
            // If we just deleted the active one, clear selection so _load picks the default
            if (this.state.activeStreamId === streamId) this.state.activeStreamId = null;
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _saveTopicSchedule(topicId, dateStr) {
        if (!topicId) return;
        try {
            const iso = dateStr ? new Date(dateStr + 'T00:00:00').toISOString() : null;
            await apRequest('/api/autopilot/schedule-topic', { brandId: this.brandId, topicId, scheduledFor: iso });
            toast('success', dateStr ? ('Scheduled for ' + dateStr) : 'Schedule cleared');
            this.state.topicPickerOpenId = null;
            await this._load();
        } catch (e) { toast('error', e.message); }
    }

    async _deleteTopic(topicId) {
        if (!topicId) return;
        if (this.state.deletingTopicIds.has(topicId)) return;
        if (!await confirmModal({ title: 'Delete topic', message: 'Delete this topic from the queue? This cannot be undone.', confirmLabel: 'Delete', danger: true })) return;
        this.state.deletingTopicIds.add(topicId);
        this._render();
        try {
            await apRequest('/api/autopilot/delete-topic', { brandId: this.brandId, topicId });
            toast('success', 'Topic removed');
            // If the deleted topic was expanded, collapse it first
            if (this.state.inspectedTopicId === topicId) {
                this.state.inspectedTopicId = null;
                this.state.inspectedTopicData = null;
            }
            await this._load();
        } catch (e) {
            toast('error', e.message);
        } finally {
            this.state.deletingTopicIds.delete(topicId);
            this._render();
        }
    }

    _render() {
        if (!this.el) return;
        const header = renderPageHeader({
            title: 'Content Autopilot',
            subtitle: 'A weekly blog cycle + cross-platform promo cascade, running on the Brain.',
        });

        if (this.state.loading) {
            this.el.innerHTML = `<div class="ap-scope">${header}</div>` + `<div class="ap-page"><div class="ap-loading">Loading…</div></div>${renderStyles()}`;
            return;
        }

        if (this.state.error || !this.state.data) {
            this.el.innerHTML = `<div class="ap-scope">${header}</div>` + `
              <div class="ap-page">
                <div class="ap-card ap-card--compact">
                  <div class="ap-info">${this.state.error || 'No data'}</div>
                </div>
              </div>
              ${renderStyles()}`;
            return;
        }

        const { settings, next, proposedTopics, upcomingQueue, hasByok, byokProviders } = this.state.data;
        const isEnabled = !!settings.enabled;
        const isPaused = settings.paused_until && new Date(settings.paused_until) > new Date();

        this.el.innerHTML = `
            <div class="ap-scope">${header}</div>
            <div class="ap-page">

              <!-- Status ribbon -->
              <div class="ap-ribbon ${isEnabled ? (isPaused ? 'ap-ribbon--paused' : 'ap-ribbon--on') : 'ap-ribbon--off'}">
                <span class="ap-dot-lg"></span>
                <div class="ap-ribbon__label" aria-live="polite">
                  ${isEnabled
                    ? (isPaused
                        ? 'Paused'
                        : 'On · ' + ((PRESET_LABELS[settings.preset] || PRESET_LABELS.assisted).label))
                    : 'Off'}
                </div>
                ${isEnabled && !isPaused ? `<div class="ap-ribbon__explain">${(PRESET_LABELS[settings.preset] || PRESET_LABELS.assisted).oneLiner}</div>` : ''}
                <div class="ap-ribbon__actions">
                  ${isEnabled
                    ? (isPaused
                        ? `<button class="ap-btn ap-btn--ghost" data-action="resume">Resume</button>`
                        : `<div class="ap-pause-wrap">
                             <button class="ap-btn ap-btn--ghost ap-pause-main" data-action="pause" title="Pause for 7 days">Pause 7 days</button>
                             <button class="ap-btn ap-btn--ghost ap-pause-caret" data-action="toggle-pause-menu" title="Other pause durations" aria-haspopup="true" aria-expanded="${this.state.pauseMenuOpen ? 'true' : 'false'}">▾</button>
                             ${this.state.pauseMenuOpen ? `
                               <div class="ap-pause-menu" role="menu">
                                 <button class="ap-pause-menu__item" data-action="pause-duration" data-days="7">Pause for 7 days</button>
                                 <button class="ap-pause-menu__item" data-action="pause-duration" data-days="30">Pause for 30 days</button>
                                 <button class="ap-pause-menu__item" data-action="pause-duration" data-days="9999">Pause indefinitely</button>
                               </div>
                             ` : ''}
                           </div>`)
                    : ''
                  }
                  ${isEnabled ? `<button class="ap-btn ap-btn--ghost" data-action="open-settings" title="Change publish day, time, destinations, and platforms">Schedule &amp; platforms</button>` : ''}
                  <button class="ap-btn ap-btn--ghost" data-action="open-chat">Chat with the Brain →</button>
                </div>
              </div>
              ${isEnabled && !isPaused ? this._renderNextPublishLine(settings) : ''}

              ${!hasByok ? this._renderByokMissingCard(byokProviders) : ''}
              ${!isEnabled ? this._renderPresetPicker() : this._renderActiveView(next, proposedTopics, upcomingQueue, settings)}

              <!-- Tech note -->
              <section class="ap-card ap-card--compact">
                <div class="ap-info">
                  <strong>Your AI, your bill.</strong> Autopilot runs on your own AI provider key
                  (OpenAI, Anthropic, or Google) — Heist never charges per generation. Interactive
                  chat via Claude Desktop or ChatGPT works without a key; autonomous autopilot needs one.
                  <a href="#settings?tab=ai">Connect your AI →</a>
                </div>
              </section>
            </div>
            ${renderStyles()}
        `;

        // Pass 3: scroll the edited topic row into view on mount
        if (this.state.editedTopicId) {
            requestAnimationFrame(() => {
                const row = this.el.querySelector(`[data-topic-row-id="${this.state.editedTopicId}"]`);
                if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }

        // Bind
        this.el.querySelectorAll('[data-action]').forEach(btn => {
            btn.addEventListener('click', e => {
                // For overlay/backdrop close actions (close-score-modal / close-settings),
                // only fire when the click landed DIRECTLY on the overlay, not on
                // a nested child (the modal content). Everything else fires normally.
                const action = btn.dataset.action;
                if ((action === 'close-settings' || action === 'close-new-stream')
                    && e.target !== btn) {
                    return;
                }
                if (this.state && this.state.pauseMenuOpen
                    && action !== 'toggle-pause-menu'
                    && action !== 'pause-duration') {
                    this.state.pauseMenuOpen = false;
                }
                if (this.state && this.state.topicPickerOpenId
                    && action !== 'toggle-topic-picker'
                    && action !== 'save-topic-schedule'
                    && action !== 'clear-topic-schedule') {
                    this.state.topicPickerOpenId = null;
                }
                if (this.state && this.state.chipPopover
                    && action !== 'open-chip-schedule'
                    && action !== 'open-chip-destinations'
                    && action !== 'open-chip-platforms'
                    && action !== 'close-chip-pop'
                    && action !== 'save-chip-schedule'
                    && action !== 'save-chip-destinations'
                    && action !== 'save-chip-platforms') {
                    this.state.chipPopover = null;
                }
                // If the click originated on a nested [data-action] element
                // whose action differs from this listener's, skip — the nested
                // element has its own listener and we don't want to double-fire
                // the outer row's action (e.g. clicking delete should not
                // also toggle inspect).
                const inner = e.target.closest('[data-action]');
                if (inner && inner !== btn) return;
                this._onAction(action, btn.dataset);
            });
        });
    }

    _renderByokMissingCard(byokProviders) {
        return '<section class="ap-card ap-card--warn"><div class="ap-card__rail" style="--rail:linear-gradient(180deg,#EF4444,#DC2626)"></div><header class="ap-card__head"><div class="ap-card__badge" style="color:var(--danger-text);border-color:rgba(239,68,68,0.3);background:rgba(239,68,68,0.08);">Action needed</div><h3>Connect an AI to run autopilot</h3><p>Autopilot writes blogs and social promos on a schedule - it needs an AI provider (OpenAI, Anthropic, or Google) to call. You bring the key, you keep the bill. Heist never charges per generation.</p></header><div class="ap-actions"><a href="#settings?tab=ai" class="ap-btn ap-btn--primary">Connect your AI &rarr;</a><button class="ap-btn ap-btn--ghost" data-action="open-chat">Ask the Brain which to pick</button></div></section>';
    }

    _renderPresetPicker() {
        return `
          <section class="ap-card ap-card--primary">
            <div class="ap-card__rail" style="--rail:linear-gradient(180deg,#D97706,#F59E0B)"></div>
            <header class="ap-card__head">
              <h3>Turn on Content Autopilot</h3>
              <p>Pick a starting preset — you can tweak anything later from the advanced settings or by chatting with the Brain.</p>
            </header>
            <div class="ap-presets">
              ${this._preset('manual_review', 'Manual review', 'You approve every blog and every social promo')}
              ${this._preset('assisted',      'Assisted',      'You approve the blog, promos auto-fire', true)}
              ${this._preset('autonomous',    'Autonomous',    'Nothing waits for you — fully hands-off')}
            </div>
          </section>
        `;
    }

    _preset(id, label, desc, rec = false) {
        const rules = PRESET_RULES[id] || [];
        const rulesHtml = rules.length ? `
            <details class="ap-preset__rules">
                <summary>What this actually means</summary>
                <ul>${rules.map(r => `<li>${escapeHtml(r)}</li>`).join('')}</ul>
            </details>
        ` : '';
        return `
          <div class="ap-preset-wrap">
            <button class="ap-preset" data-action="enable" data-preset="${id}" data-testid="ap-preset-${id}">
                ${rec ? '<span class="ap-preset__rec">Recommended</span>' : ''}
                <div class="ap-preset__label">${label}</div>
                <div class="ap-preset__desc">${desc}</div>
            </button>
            ${rulesHtml}
          </div>
        `;
    }

    _renderActiveView(next, proposedTopics, upcomingQueue, settings) {
        // Is there a drafted blog ready for review?
        const draftedBlogId = (next && next.blog_post_id) ? next.blog_post_id : null;
        const draftBadge = draftedBlogId ? 'Ready to approve' : (next && next._isCandidate ? 'Top candidate' : 'Next post');
        const nextCard = next ? `
          <section class="ap-card ap-card--primary">
            <div class="ap-card__rail" style="--rail:linear-gradient(180deg,#D97706,#F59E0B)"></div>
            <header class="ap-card__head">
              <div class="ap-card__badge">${draftBadge}</div>
              <h3>${escapeHtml(next.title || '(no title)')}</h3>
              ${next.hook ? `<p>${escapeHtml((next.hook || '').slice(0, 220))}</p>` : ''}
            </header>
            <div class="ap-actions">
              ${draftedBlogId
                ? `<a class="ap-btn ap-btn--primary" href="#create?blogId=${escapeHtml(draftedBlogId)}&mode=blog">Review &amp; approve &rarr;</a>`
                : ''}
              <button class="ap-btn ap-btn--ghost" data-action="chat-about-topic" data-topic-id="${escapeHtml(next.id || '')}" data-topic-title="${escapeHtml(next.title || '')}">Chat about this with the Brain</button>
              ${this.state.seeding ? '<button class="ap-btn ap-btn--ghost" disabled><span class="ap-spinner"></span>Thinking…</button>' : '<button class="ap-btn ap-btn--ghost" data-action="seed-now">Get fresh topic ideas</button>'}
              <button class="ap-btn ap-btn--ghost" data-action="send-veto-now" data-testid="ap-send-veto-now" title="Send the topic-veto notification now (so you can review before it auto-publishes)">Send veto now</button>
            </div>
            ${draftedBlogId ? `<div class="ap-cascade-preview">On approval, promo drafts auto-fire for: <strong>${(settings.social_platforms || []).map(p => p.toUpperCase()).join(', ') || '(none — add platforms in settings)'}</strong> at each platform's optimal time.</div>` : ''}
          </section>
        ` : `
          <section class="ap-card">
            <div class="ap-info">No topic queued yet.
              ${this.state.seeding ? '<button class="ap-btn ap-btn--primary ap-btn--sm" style="margin-left:12px" disabled><span class="ap-spinner"></span>Thinking…</button>' : '<button class="ap-btn ap-btn--primary ap-btn--sm" style="margin-left:12px" data-action="seed-now">Seed now</button>'}
              <button class="ap-btn ap-btn--ghost ap-btn--sm" style="margin-left:8px" data-action="send-veto-now" data-testid="ap-send-veto-now" title="Send the topic-veto notification now">Send veto now</button>
            </div>
          </section>
        `;

        const vetoList = (proposedTopics && proposedTopics.length > 0) ? `
          <section class="ap-card">
            <header class="ap-card__head">
              <h3>Pick the next topic</h3>
              <p>If you don't pick, the Brain will pick its top choice automatically before Monday.</p>
            </header>
            <div class="ap-veto-list">
              ${proposedTopics.map((t, i) => `
                <div class="ap-veto-item">
                  <div class="ap-veto-rank">#${i + 1}</div>
                  <div class="ap-veto-body">
                    <div class="ap-veto-title">${escapeHtml(t.title || '')}</div>
                    ${t.hook ? `<div class="ap-veto-hook">${escapeHtml((t.hook || '').slice(0, 180))}</div>` : ''}
                    <div class="ap-veto-score">score ${(Number(t.score) * 100).toFixed(0)}</div>
                  </div>
                  <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="pick-topic" data-topic-id="${escapeHtml(t.id)}">Pick this</button>
                </div>
              `).join('')}
            </div>
          </section>
        ` : '';

        // If viewing a non-default stream, show a placeholder (per-stream detail in Phase C)
        const streamsArr = Array.isArray(this.state.data?.streams) ? this.state.data.streams : [];
        const activeStream = streamsArr.find(x => x.id === this.state.activeStreamId);
        const isDefault = !activeStream || activeStream.isDefault;
        const streamsCard = this._renderStreamsCard();
        if (!isDefault) {
            return `${streamsCard}${this._renderStreamDetail(activeStream)}`;
        }
        const welcomeCard = this._renderFirstVisitCard();
        const editedBanner = this.state.showEditedBanner ? `
          <div class="ap-edit-banner">
            <span class="ap-edit-banner__dot">✓</span>
            <span>Updated from chat. Scroll to the highlighted topic to review.</span>
            <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="dismiss-edit-banner">Dismiss</button>
          </div>` : '';
        return `${welcomeCard}${editedBanner}${this._renderReflectionCard()}${streamsCard}${nextCard}${this._renderQueueCard(upcomingQueue, proposedTopics)}${this._renderCustomSourcesCard()}${this._renderSettingsModal()}`;
    }

    async _inspectTopic(topicId) {
        if (!topicId) return;
        // Toggle off if already expanded for this topic
        if (this.state.inspectedTopicId === topicId) {
            this.state.inspectedTopicId = null;
            this.state.inspectedTopicData = null;
            this._render();
            return;
        }
        // Optimistic: show a loading row underneath while we fetch
        this.state.inspectedTopicId = topicId;
        this.state.inspectedTopicData = null;
        this._render();
        try {
            const r = await apRequest('/api/autopilot/topic/' + encodeURIComponent(topicId));
            if (this.state.inspectedTopicId === topicId) {
                this.state.inspectedTopicData = r.topic;
                // The server may have backfilled + updated the stored score.
                // Patch the outer list entry so the bubble matches the breakdown.
                if (this.state.data && r.topic && typeof r.topic.score === 'number') {
                    const patch = (arr) => {
                        if (!Array.isArray(arr)) return;
                        const hit = arr.find(t => t.id === topicId);
                        if (hit) hit.score = r.topic.score;
                    };
                    patch(this.state.data.upcomingQueue);
                    patch(this.state.data.proposedTopics);
                }
                this._render();
            }
        } catch (e) {
            toast('error', 'Could not load topic: ' + (e.message || 'unknown'));
            if (this.state.inspectedTopicId === topicId) {
                this.state.inspectedTopicId = null;
                this._render();
            }
        }
    }

        _renderSourcesHelpPopover() {
        const defaults = Array.isArray(this.state.data && this.state.data.industryDefaults) ? this.state.data.industryDefaults : [];
        return `
          <div class="ap-src-help-pop">
            <div class="ap-src-help-pop__title">Always included for you</div>
            <p class="ap-src-help-pop__intro">Based on your brand’s industry, the Brain already scans these. You don’t have to add them manually.</p>
            ${defaults.length ? `<ul class="ap-src-help-list">${defaults.slice(0, 20).map(d => `<li><span class="ap-src-help-type">${escapeHtml(d.type || 'feed')}</span> ${escapeHtml(d.label || d.url || d.sub || '')}</li>`).join('')}</ul>` : '<p class="ap-src-help-pop__empty">No industry defaults found (set an industry in Schedule &amp; platforms to enable).</p>'}
            <div class="ap-src-help-pop__foot">
              <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="toggle-sources-help">Close</button>
            </div>
          </div>
        `;
    }

    _renderCustomSourcesCard() {
        const sources = this.state.customSources || [];
        const rows = sources.length ? sources.map(s => `
          <li class="ap-src-item ${s.active ? '' : 'ap-src-item--off'}">
            <span class="ap-src-type">${escapeHtml(s.type)}</span>
            <span class="ap-src-label">${escapeHtml(s.label || s.url || s.sub || '(unnamed)')}</span>
            <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="toggle-src" data-src-id="${escapeHtml(s.id)}" data-src-active="${s.active ? '0' : '1'}">${s.active ? 'Disable' : 'Enable'}</button>
            <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="delete-src" data-src-id="${escapeHtml(s.id)}">Remove</button>
          </li>
        `).join('') : '<li class="ap-src-empty">No custom sources yet — the Brain is using your industry bundle only.</li>';

        return `
          <section class="ap-card">
            <header class="ap-card__head">
              <h3>Your sources
                <button class="ap-src-help-btn" data-action="toggle-sources-help" title="What sources are always included?" aria-label="Show industry defaults">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </button>
              </h3>
              <p>RSS feeds, Reddit subs, or your own content the Brain reads when researching topics. Industry defaults are always included.</p>
              ${this.state.sourcesHelpOpen ? this._renderSourcesHelpPopover() : ''}
            </header>
            <ul class="ap-src-list">${rows}</ul>
            <div class="ap-src-add">
              <input type="text" id="ap-src-url" class="ap-input" placeholder="Website, RSS URL, or reddit sub (e.g. heistbrain.com)" />
              <select id="ap-src-type" class="ap-input">
                <option value="url">Website / URL (auto-detect RSS + sitemap)</option>
                <option value="rss">RSS feed</option>
                <option value="reddit">Reddit sub</option>
              </select>
              <input type="text" id="ap-src-label" class="ap-input ap-input--short" placeholder="Label (optional)" />
              <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="add-src">Add source</button>
            </div>
          </section>
        `;
    }

    async _loadCustomSources() {
        if (!this.brandId) return;
        try {
            const r = await apRequest('/api/autopilot/custom-sources?brandId=' + encodeURIComponent(this.brandId));
            this.state.customSources = r.sources || [];
        } catch (e) { this.state.customSources = []; }
    }

    async _addCustomSource() {
        let urlOrSub = this.el.querySelector('#ap-src-url')?.value?.trim();
        const type = this.el.querySelector('#ap-src-type')?.value || 'rss';
        const label = this.el.querySelector('#ap-src-label')?.value?.trim();
        if (!urlOrSub) { toast('error', 'Enter a URL or subreddit name'); return; }
        // For url/rss types, auto-prefix https:// if missing
        if ((type === 'url' || type === 'rss') && !/^https?:\/\//i.test(urlOrSub)) {
            urlOrSub = 'https://' + urlOrSub;
        }
        try {
            const body = type === 'reddit'
                ? { brandId: this.brandId, type, sub: urlOrSub.replace(/^r\//i, ''), label }
                : { brandId: this.brandId, type, url: urlOrSub, label };
            await apRequest('/api/autopilot/custom-sources', body);
            toast('success', type === 'url'
                ? 'Website added — the Brain will scan it (sitemap, RSS, or article links) on the next research cycle'
                : 'Source added — will be used in the next research cycle');
            // Clear the inputs
            if (this.el.querySelector('#ap-src-url')) this.el.querySelector('#ap-src-url').value = '';
            if (this.el.querySelector('#ap-src-label')) this.el.querySelector('#ap-src-label').value = '';
            await this._loadCustomSources();
            this._render();
        } catch (e) { toast('error', e.message); }
    }

    async _toggleSource(srcId, active) {
        try {
            await apRequest('/api/autopilot/custom-sources/' + encodeURIComponent(srcId) + '/toggle',
                { brandId: this.brandId, active: active === '1' });
            await this._loadCustomSources();
            this._render();
        } catch (e) { toast('error', e.message); }
    }

    async _deleteSource(srcId) {
        if (!await confirmModal({ title: 'Remove source', message: 'Remove this source from research?', confirmLabel: 'Remove', danger: true })) return;
        try {
            const res = await fetch('/api/autopilot/custom-sources/' + encodeURIComponent(srcId) + '?brandId=' + encodeURIComponent(this.brandId), {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'x-csrf-token': (document.cookie.split('; ').find(c => c.startsWith('csrf_token=')) || '').split('=')[1] || '',
                },
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.error || res.statusText);
            toast('success', 'Source removed');
            await this._loadCustomSources();
            this._render();
        } catch (e) { toast('error', e.message); }
    }

    _renderStreamDetail(stream) {
        if (!stream) return '';
        const type = stream.type || 'blog_cycle';
        const cfg = stream.config || {};
        const sched = stream.schedule || {};
        const paused = stream.pausedUntil && new Date(stream.pausedUntil) > new Date();
        const stateLabel = !stream.enabled ? 'Off' : paused ? 'Paused' : 'On';

        let typeBody = '';
        if (type === 'campaign') {
            const total = (Array.isArray(cfg.platforms) ? cfg.platforms.length : 0) * (Number(cfg.posts_per_platform) || 0);
            const generated = Array.isArray(cfg.generatedIds) ? cfg.generatedIds.length : 0;
            const pct = total ? Math.round((generated / total) * 100) : 0;
            typeBody = `
                <div class="ap-det-row"><span>Brief</span><span>${escapeHtml(String(cfg.brief || '').slice(0, 240))}</span></div>
                <div class="ap-det-row"><span>Platforms</span><span>${(cfg.platforms || []).map(escapeHtml).join(', ') || '—'}</span></div>
                <div class="ap-det-row"><span>Progress</span><span>${generated} / ${total || '?'} drafts</span></div>
                ${total ? `<div class="ap-det-bar"><div class="ap-det-bar__fill" style="width:${pct}%"></div></div>` : ''}
                <div class="ap-det-row"><span>End date</span><span>${cfg.end_date ? escapeHtml(new Date(cfg.end_date).toLocaleDateString()) : '—'}</span></div>
                ${cfg.generatedAt ? `<div class="ap-det-row"><span>Generated at</span><span>${escapeHtml(new Date(cfg.generatedAt).toLocaleString())}</span></div>` : '<div class="ap-det-row ap-det-pending"><span>Status</span><span>Will generate within the next 15 minutes</span></div>'}
            `;
        } else if (type === 'social_only') {
            const bank = Array.isArray(cfg.topic_bank) ? cfg.topic_bank : [];
            typeBody = `
                <div class="ap-det-row"><span>Topic bank</span><span>${bank.length} theme${bank.length === 1 ? '' : 's'}</span></div>
                <ul class="ap-det-list">${bank.slice(0, 6).map(t => `<li>${escapeHtml(t)}</li>`).join('')}</ul>
                <div class="ap-det-row"><span>Platforms</span><span>${(cfg.platforms || []).map(escapeHtml).join(', ') || '—'}</span></div>
                <div class="ap-det-row"><span>Cadence</span><span>${Number(cfg.cadence_per_week) || 3}&times;/week</span></div>
                <div class="ap-det-row"><span>Drafts generated so far</span><span>${Number(cfg.generatedCount) || 0}</span></div>
                <div class="ap-det-row"><span>Last run</span><span>${cfg.lastRunAt ? escapeHtml(new Date(cfg.lastRunAt).toLocaleString()) : 'never'}</span></div>
            `;
        } else if (type === 'report') {
            typeBody = `
                <div class="ap-det-row"><span>Cadence</span><span>${escapeHtml(cfg.cadence || 'monthly')}</span></div>
                <div class="ap-det-row"><span>Email delivery</span><span>${cfg.deliver_email ? 'yes' : 'no'}</span></div>
                <div class="ap-det-row"><span>Runs completed</span><span>${Number(cfg.runCount) || 0}</span></div>
                <div class="ap-det-row"><span>Last run</span><span>${cfg.lastRunAt ? escapeHtml(new Date(cfg.lastRunAt).toLocaleString()) : 'never'}</span></div>
                ${cfg.lastResult ? `<div class="ap-det-row"><span>Last report</span><span>${escapeHtml(cfg.lastResult.year + '-' + cfg.lastResult.month)} &middot; ${cfg.lastResult.insights || 0} insights</span></div>` : ''}
            `;
        } else {
            typeBody = `<p class="ap-wiz-hint">No detail view for type <code>${escapeHtml(type)}</code> yet.</p>`;
        }

        return `
          <section class="ap-card">
            <header class="ap-card__head">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
                <div>
                  <h3>${escapeHtml(stream.name)}</h3>
                  <p>${escapeHtml(type.replace('_', ' '))} &middot; ${escapeHtml(stateLabel)} &middot; ${escapeHtml((PRESET_LABELS[stream.preset] || PRESET_LABELS.assisted).label)}</p>
                </div>
              </div>
            </header>
            <div class="ap-det-body">${typeBody}</div>
          </section>
        `;
    }

    _renderStreamsCard() {
        const streams = Array.isArray(this.state.data?.streams) ? this.state.data.streams : [];
        if (!streams.length) return '';
        const activeId = this.state.activeStreamId;
        const cards = streams.map(st => {
            const isActive = st.id === activeId;
            const paused = st.pausedUntil && new Date(st.pausedUntil) > new Date();
            const stateLabel = !st.enabled ? 'Off' : paused ? 'Paused' : 'On';
            const stateClass = !st.enabled ? 'ap-str-state--off' : paused ? 'ap-str-state--paused' : 'ap-str-state--on';
            const typeBadge = (st.type || 'blog_cycle').replace('_', ' ');
            const controls = [];
            if (st.enabled && !paused) {
                controls.push(`<button class="ap-str-ctl" data-action="pause-stream" data-stream-id="${escapeHtml(st.id)}" title="Pause 7 days">Pause</button>`);
            } else if (paused) {
                controls.push(`<button class="ap-str-ctl" data-action="resume-stream" data-stream-id="${escapeHtml(st.id)}" title="Resume">Resume</button>`);
            }
            if (!st.isDefault) {
                controls.push(`<button class="ap-str-ctl ap-str-ctl--danger" data-action="delete-stream" data-stream-id="${escapeHtml(st.id)}" title="Delete stream">Delete</button>`);
            }
            return `
              <div class="ap-str-card ${isActive ? 'ap-str-card--active' : ''}" data-action="select-stream" data-stream-id="${escapeHtml(st.id)}">
                <div class="ap-str-head">
                  <div class="ap-str-name-wrap">
                    <div class="ap-str-name">${escapeHtml(st.name)}${st.isDefault ? ' <span class="ap-str-default">default</span>' : ''}</div>
                    <div class="ap-str-meta"><span class="ap-str-type">${escapeHtml(typeBadge)}</span> · ${escapeHtml((PRESET_LABELS[st.preset] || PRESET_LABELS.assisted).label)}</div>
                  </div>
                  <div class="ap-str-state ${stateClass}">${stateLabel}</div>
                </div>
                ${controls.length ? `<div class="ap-str-ctls">${controls.join('')}</div>` : ''}
              </div>
            `;
        }).join('');
        return `
          <section class="ap-card ap-str-list-card">
            <header class="ap-card__head">
              <div class="ap-str-head-row">
                <div>
                  <h3>Streams</h3>
                  <p>Every automated workflow running for this brand. Click a stream to see its detail below.</p>
                </div>
                <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="open-new-stream">+ New stream</button>
              </div>
            </header>
            <div class="ap-str-grid">${cards}</div>
            ${this.state.newStreamOpen ? this._renderNewStreamModal() : ''}
          </section>
        `;
    }

    _renderNewStreamModal() {
        const disabled = this.state.creatingStream;
        const t = this.state.newStreamType || 'blog_cycle';
        const typeForms = {
            blog_cycle: `
                <p class="ap-wiz-hint">A weekly blog cycle: the Brain proposes topics, you pick one, it drafts + schedules promos. Configure schedule details later from the stream's settings.</p>
            `,
            campaign: `
                <label class="ap-modal__label">Campaign brief
                  <textarea id="ap-wiz-brief" rows="4" class="ap-modal__input" placeholder="e.g. Q2 product launch — 10 posts across LinkedIn and Instagram highlighting our new AI feature" ${disabled ? 'disabled' : ''}></textarea>
                </label>
                <label class="ap-modal__label">Platforms (comma-separated)
                  <input id="ap-wiz-platforms" type="text" class="ap-modal__input" value="linkedin,instagram" ${disabled ? 'disabled' : ''} />
                </label>
                <div class="ap-wiz-row">
                  <label class="ap-modal__label" style="flex:1">Posts per platform
                    <input id="ap-wiz-ppp" type="number" min="1" max="15" value="5" class="ap-modal__input" ${disabled ? 'disabled' : ''} />
                  </label>
                  <label class="ap-modal__label" style="flex:1">End date
                    <input id="ap-wiz-end" type="date" class="ap-modal__input" ${disabled ? 'disabled' : ''} />
                  </label>
                </div>
                <p class="ap-wiz-hint">Drafts generate within ~15 minutes. They land in your inbox for approval before they go live.</p>
            `,
            social_only: `
                <label class="ap-modal__label">Topic bank (one theme per line)
                  <textarea id="ap-wiz-bank" rows="4" class="ap-modal__input" placeholder="productivity tips\nteam management\ncustomer stories" ${disabled ? 'disabled' : ''}></textarea>
                </label>
                <label class="ap-modal__label">Platforms (comma-separated)
                  <input id="ap-wiz-platforms" type="text" class="ap-modal__input" value="linkedin,twitter" ${disabled ? 'disabled' : ''} />
                </label>
                <label class="ap-modal__label">Posts per week (per platform)
                  <input id="ap-wiz-cadence" type="number" min="1" max="14" value="3" class="ap-modal__input" ${disabled ? 'disabled' : ''} />
                </label>
                <p class="ap-wiz-hint">The stream rotates through your topic bank and drafts one post per platform at your configured cadence. No blog anchor.</p>
            `,
            report: `
                <label class="ap-modal__label">Cadence
                  <select id="ap-wiz-cadence-sel" class="ap-modal__input" ${disabled ? 'disabled' : ''}>
                    <option value="monthly" selected>Monthly — full performance reflection</option>
                    <option value="weekly">Weekly — engagement digest</option>
                  </select>
                </label>
                <label class="ap-modal__label" style="display:flex;align-items:center;gap:10px;text-transform:none;letter-spacing:0;">
                  <input id="ap-wiz-email" type="checkbox" ${disabled ? 'disabled' : ''} />
                  <span style="font-size:14px;color:var(--text);">Also deliver via email</span>
                </label>
                <p class="ap-wiz-hint">The monthly reflection analyzes last month's performance and saves insights to the Brain. Weekly digests are a lightweight summary (wired up in the next update).</p>
            `,
        };
        return `
          <div class="ap-modal-overlay" data-action="close-new-stream">
            <div class="ap-modal ap-modal--wizard">
              <h4>New stream</h4>
              <p class="ap-modal__sub">Pick a workflow and give it a name. You can fine-tune the schedule and details after it's created.</p>
              <label class="ap-modal__label">Name
                <input id="ap-new-stream-name" type="text" placeholder="e.g. Q2 product launch" class="ap-modal__input" ${disabled ? 'disabled' : ''} />
              </label>
              <label class="ap-modal__label">Type
                <select id="ap-new-stream-type" class="ap-modal__input" data-action="change-stream-type" ${disabled ? 'disabled' : ''}>
                  <option value="blog_cycle"${t==='blog_cycle'?' selected':''}>Weekly blog cycle</option>
                  <option value="campaign"${t==='campaign'?' selected':''}>Time-bound campaign</option>
                  <option value="social_only"${t==='social_only'?' selected':''}>Social-only cadence</option>
                  <option value="report"${t==='report'?' selected':''}>Report digest</option>
                </select>
              </label>
              <div class="ap-wiz-form">${typeForms[t] || ''}</div>
              <div class="ap-modal__actions">
                <button class="ap-btn ap-btn--ghost" data-action="close-new-stream" ${disabled ? 'disabled' : ''}>Cancel</button>
                <button class="ap-btn ap-btn--primary" data-action="submit-new-stream" ${disabled ? 'disabled' : ''}>
                  ${disabled ? '<span class="ap-spinner"></span>Creating…' : 'Create stream'}
                </button>
              </div>
            </div>
          </div>
        `;
    }

    _renderQueueCard(queue, proposedTopics) {
        // Merge upcomingQueue (full queue) with proposedTopics (top-5 with hooks).
        // Build a hook lookup from proposedTopics so rows can show excerpts.
        const hookById = {};
        (proposedTopics || []).forEach(p => { if (p && p.id) hookById[p.id] = p.hook || ''; });

        const items = (queue || []).slice(0, 8);
        if (!items.length && (!proposedTopics || !proposedTopics.length)) return '';

        const expandedId = this.state.inspectedTopicId;
        const pickerOpenId = this.state.topicPickerOpenId || null;

        const rows = items.map((q, idx) => {
            const status = q.status || 'proposed';
            const statusLabel = status === 'chosen' ? 'chosen'
                              : status === 'drafted' ? 'drafted'
                              : 'alternate';
            const statusClass = status === 'chosen' ? 'ap-q-status--chosen'
                              : status === 'drafted' ? 'ap-q-status--active'
                              : '';
            const isExpanded = expandedId === q.id;
            const isDeleting = this.state.deletingTopicIds.has(q.id);
            const hook = hookById[q.id] || '';
            const schedDate = q.scheduled_for ? new Date(q.scheduled_for) : null;
            const schedLabel = schedDate ? schedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';

            const actions = [];
            // Per-topic schedule (calendar)
            const isPickerOpen = pickerOpenId === q.id;
            actions.push(`<div class="ap-q-sched">
              <button class="ap-q-ico" data-action="toggle-topic-picker" data-topic-id="${escapeHtml(q.id)}" title="${schedLabel ? 'Scheduled for ' + schedLabel + ' (click to change)' : 'Schedule for a specific date'}" aria-label="Schedule date">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                ${schedLabel ? `<span class="ap-q-sched-label">${escapeHtml(schedLabel)}</span>` : ''}
              </button>
              ${isPickerOpen ? `
                <div class="ap-q-picker">
                  <label>Publish this topic on
                    <input type="date" id="ap-topic-date-${escapeHtml(q.id)}" value="${q.scheduled_for ? new Date(q.scheduled_for).toISOString().slice(0,10) : this._nextPublishDate()}" />
                  </label>
                  <div class="ap-q-picker-actions">
                    ${q.scheduled_for ? `<button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="clear-topic-schedule" data-topic-id="${escapeHtml(q.id)}">Clear</button>` : ''}
                    <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="save-topic-schedule" data-topic-id="${escapeHtml(q.id)}">Save</button>
                  </div>
                </div>
              ` : ''}
            </div>`);
            // Pick this (only if not already chosen/drafted)
            if (status === 'proposed') {
                actions.push(`<button class="ap-btn ap-btn--primary ap-btn--sm" data-action="pick-topic" data-topic-id="${escapeHtml(q.id)}" title="Make this the next blog post">Pick this</button>`);
            }
            // Delete
            actions.push(isDeleting
                ? '<button class="ap-q-del" disabled title="Deleting…"><span class="ap-spinner ap-spinner--sm"></span></button>'
                : `<button class="ap-q-del" data-action="delete-topic" data-topic-id="${escapeHtml(q.id)}" title="Remove from queue" aria-label="Remove topic"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>`);

            const headRow = `
              <div class="ap-q-head ${isExpanded ? 'ap-q-head--expanded' : ''}" data-action="inspect-topic" data-topic-id="${escapeHtml(q.id)}">
                <span class="ap-q-rank">#${idx + 1}</span>
                <div class="ap-q-titlewrap">
                  <div class="ap-q-title-line"><span class="ap-q-status ${statusClass}">${statusLabel}</span><span class="ap-q-title">${escapeHtml((q.title || '').slice(0, 120))}</span></div>
                  ${hook ? `<div class="ap-q-hook">${escapeHtml(hook.slice(0, 160))}</div>` : ''}
                </div>
                <span class="ap-q-score">${q.score != null ? Math.round(Number(q.score) * 100) : '—'}</span>
                <div class="ap-q-actions" onclick="event.stopPropagation()">${actions.join('')}</div>
                <span class="ap-q-chev">${isExpanded ? '⌄' : '›'}</span>
              </div>`;
            const inlineDetail = isExpanded ? `<div class="ap-q-inline-detail">${this._renderInlineBreakdown(this.state.inspectedTopicData)}</div>` : '';
            const flashCls = this.state.editedTopicId === q.id ? ' ap-q-item--flash' : '';
            return `<li class="ap-q-item ap-q-item--card ${isExpanded ? 'ap-q-item--expanded' : ''}${flashCls}" data-topic-row-id="${escapeHtml(q.id)}">${headRow}${inlineDetail}</li>`;
        }).join('');

        return `<section class="ap-card"><header class="ap-card__head"><h3>This week’s topics</h3><p>The Brain’s picks for upcoming cycles. Click Pick this to swap the next post, the calendar to schedule a specific date, or the row to see the score breakdown.</p></header><ul class="ap-q-list ap-q-list--cards">${rows}</ul></section>`;
    }

    _renderInlineBreakdown(topic) {
        if (!topic) {
            return '<div class="ap-inline-loading">Loading…</div>';
        }
        const breakdown = topic.scoreBreakdown || {};
        const rows = [
            { key: 'source',      label: 'Trusted source',        val: breakdown.source,      help: 'Where this topic came from. Curated industry feeds and your own top posts score higher.' },
            { key: 'quality',     label: 'Quality of content',    val: breakdown.quality,     help: 'How substantive the pulled content is — title depth, summary richness, URL presence, freshness.' },
            { key: 'audience',    label: 'Matches your audience', val: breakdown.audience,    help: 'How well this topic overlaps with your brand audience description and SEO keywords.' },
            { key: 'coverage',    label: 'We know this topic',    val: breakdown.coverage,    help: 'How much of your brand brief (voice, personas, facts) relates to this topic.' },
            { key: 'recency',     label: 'Fresh (not a repeat)',  val: breakdown.recency,     help: 'Bonus for topics that are new for you. Drops toward zero if similar to recent posts.' },
            { key: 'performance', label: 'Theme has worked',      val: breakdown.performance, help: 'Bonus if this theme resembles your recent top-performing posts.' },
        ];
        const sources = Array.isArray(topic.sources) ? topic.sources : [];
        const bars = rows.map(r => {
            const raw = Number(r.val || 0);
            const weight = (topic.weights && typeof topic.weights[r.key] === 'number') ? topic.weights[r.key] : (SCORE_WEIGHTS[r.key] || 0);
            const contribution = raw * weight * 100;
            const isNeg = contribution < 0 || r.negative;
            const maxAbs = Math.abs(weight) * 100;
            const widthPct = maxAbs > 0 ? Math.min(100, Math.abs(contribution) / maxAbs * 100) : 0;
            const label = (isNeg && contribution !== 0 ? '−' : '+') + Math.abs(contribution).toFixed(1) + ' / ' + maxAbs.toFixed(0);
            return `<div class="ap-score-row" title="${escapeHtml(r.help)}"><div class="ap-score-row__label">${r.label}</div><div class="ap-score-bar"><div class="ap-score-bar__fill ${isNeg ? 'ap-score-bar__fill--neg' : ''}" style="width:${widthPct}%"></div></div><div class="ap-score-row__val">${label}</div></div>`;
        }).join('');
        const runningTotal = rows.reduce((sum, r) => sum + (Number(r.val || 0) * ((topic.weights && typeof topic.weights[r.key] === 'number') ? topic.weights[r.key] : (SCORE_WEIGHTS[r.key] || 0)) * 100), 0);
        const totalDisplay = Math.max(0, Math.round(runningTotal));

        const originLabels = sources.map(s => escapeHtml(s.label || s.type || '?'));
        const all = Array.isArray(topic.allSourcesScanned) ? topic.allSourcesScanned : [];
        const originSet = new Set(sources.map(s => (s.label || s.type || '').toLowerCase()));
        const others = all.filter(s => !originSet.has((s.label || s.type || '').toLowerCase()));

        return `
          <div class="ap-detail-panel">
            <div class="ap-detail-panel__head">
              <div>
                <div class="ap-detail-panel__eyebrow">Why this scored ${totalDisplay}</div>
                <h4 class="ap-detail-panel__title">Score breakdown</h4>
              </div>
              <button class="ap-detail-close" data-action="inspect-topic" data-topic-id="${escapeHtml(topic.id)}" title="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            ${topic.hook ? `<p class="ap-detail-hook">${escapeHtml((topic.hook || '').slice(0, 400))}</p>` : ''}

            <div class="ap-detail-grid">
              <div class="ap-detail-col">
                <div class="ap-score-bars">${bars}</div>
                <div class="ap-score-total-line">
                  <span>Total</span>
                  <span class="ap-score-total-val">${totalDisplay}<span class="ap-score-total-max"> / 100</span></span>
                </div>
              </div>
              <div class="ap-detail-col ap-detail-col--meta">
                ${originLabels.length ? `
                  <div class="ap-detail-meta">
                    <div class="ap-detail-meta__label">Candidate from</div>
                    <div class="ap-detail-meta__val">${originLabels.join(' &middot; ')}</div>
                  </div>` : ''}
                ${others.length ? `
                  <div class="ap-detail-meta">
                    <div class="ap-detail-meta__label">Also scanned this cycle</div>
                    <div class="ap-detail-meta__chips">${others.slice(0,10).map(s => `<span class="ap-detail-chip">${escapeHtml(s.label || s.type)}</span>`).join('')}</div>
                  </div>` : ''}
              </div>
            </div>

            <div class="ap-detail-actions">
              <button class="ap-btn ap-btn--primary" data-action="chat-about-topic" data-topic-id="${escapeHtml(topic.id)}" data-topic-title="${escapeHtml(topic.title || '')}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px;vertical-align:-2px"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Chat about this topic
              </button>
            </div>
          </div>
        `;
    }

    _renderFirstVisitCard() {
        try {
            if (localStorage.getItem('autopilot.welcome_seen') === '1') return '';
        } catch (_) { return ''; }
        return `
          <section class="ap-welcome-card">
            <div class="ap-welcome-head">
              <div class="ap-welcome-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="3.5"/></svg>
              </div>
              <div>
                <h3>Welcome to Content Autopilot</h3>
                <p>This is where the Brain plans your week’s content for you.</p>
              </div>
              <button class="ap-welcome-dismiss" data-action="dismiss-welcome" aria-label="Dismiss">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <ol class="ap-welcome-steps">
              <li><strong>Every week</strong> the Brain proposes 5 blog topics (below). Highest scored wins unless you pick another.</li>
              <li><strong>On your publish day</strong> the top topic gets written, published to your blog destinations, and cascaded as social posts.</li>
              <li><strong>You stay in control.</strong> Pause anytime, swap topics, or click <em>Chat about this topic</em> to edit with the Brain—edits flow right back here.</li>
            </ol>
            <div class="ap-welcome-actions">
              <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="dismiss-welcome">Got it</button>
            </div>
          </section>
        `;
    }

    _nextPublishDate() {
        const data = this.state.data || {};
        const streams = Array.isArray(data.streams) ? data.streams : [];
        const active = streams.find(x => x.id === this.state.activeStreamId) || streams.find(x => x.isDefault) || null;
        const sched = (active && active.schedule) || {};
        const settings = data.settings || {};
        const dayName = String(sched.publish_day || settings.publish_day || 'monday').toLowerCase();
        const hour = Number(sched.publish_hour != null ? sched.publish_hour : (settings.publish_hour != null ? settings.publish_hour : 7));
        const dayIdx = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }[dayName] || 1;
        const now = new Date();
        const target = new Date(now);
        target.setHours(hour, 0, 0, 0);
        let delta = (dayIdx - now.getDay() + 7) % 7;
        if (delta === 0 && target.getTime() <= now.getTime()) delta = 7;
        target.setDate(now.getDate() + delta);
        const y = target.getFullYear();
        const m = String(target.getMonth() + 1).padStart(2, '0');
        const d = String(target.getDate()).padStart(2, '0');
        return y + '-' + m + '-' + d;
    }

    _renderNextPublishLine(settings) {
        const streams = Array.isArray(this.state.data && this.state.data.streams) ? this.state.data.streams : [];
        const active = streams.find(x => x.id === this.state.activeStreamId) || streams.find(x => x.isDefault) || null;
        const sched = (active && active.schedule) || {};
        const cfg = (active && active.config) || {};
        const day = sched.publish_day || settings.publish_day || 'monday';
        const hourRaw = sched.publish_hour != null ? sched.publish_hour : (settings.publish_hour != null ? settings.publish_hour : 7);
        const hour = Number(hourRaw);
        const h12 = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
        const meridiem = hour < 12 ? 'am' : 'pm';
        const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
        
        const platMap = { linkedin: 'LinkedIn', instagram: 'Instagram', x: 'X', facebook: 'Facebook', tiktok: 'TikTok', youtube: 'YouTube' };
        const destMapFriendly = { website: 'Your website blog', wordpress: 'WordPress' };
        const rawDests = Array.isArray(cfg.blog_destinations) && cfg.blog_destinations.length ? cfg.blog_destinations : (settings.blog_destinations || ['website']);
        const dests = rawDests.map(d => destMapFriendly[d] || d);
        const plats = (Array.isArray(cfg.social_platforms) && cfg.social_platforms.length ? cfg.social_platforms : (settings.social_platforms || [])).map(p => platMap[p] || p);
        const destStr = dests.length ? dests.join(', ') : 'no destination yet';
        const platStr = plats.length ? plats.join(', ') : 'no social platforms yet';
        // Inline click-to-edit popovers for each chip
        const popover = this.state.chipPopover;
        const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
        const hours = [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23];
        const destOptions = [
            { key: 'website', label: 'Your website blog (Heist-hosted)' },
            { key: 'wordpress', label: 'WordPress (your connected site)' },
        ];
        const platOptions = [
            { key: 'linkedin', label: 'LinkedIn' },
            { key: 'instagram', label: 'Instagram' },
            { key: 'x', label: 'X / Twitter' },
            { key: 'facebook', label: 'Facebook' },
            { key: 'tiktok', label: 'TikTok' },
            { key: 'youtube', label: 'YouTube' },
        ];
        const schedPop = popover === 'schedule' ? `
          <div class="ap-chip-pop">
            <div class="ap-chip-pop__title">Publish day &amp; time</div>
            <div class="ap-chip-pop__row">
              <label>Day
                <select id="ap-chip-day">
                  ${days.map(d => `<option value="${d}" ${d === day ? 'selected' : ''}>${d.charAt(0).toUpperCase() + d.slice(1)}</option>`).join('')}
                </select>
              </label>
              <label>Hour
                <select id="ap-chip-hour">
                  ${hours.map(h => {
                    const hh12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
                    const mer = h < 12 ? 'am' : 'pm';
                    return `<option value="${h}" ${h === hour ? 'selected' : ''}>${hh12}:00 ${mer}</option>`;
                  }).join('')}
                </select>
              </label>
            </div>
            <div class="ap-chip-pop__actions">
              <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="close-chip-pop">Cancel</button>
              <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="save-chip-schedule">Save</button>
            </div>
          </div>` : '';
        const destPop = popover === 'destinations' ? `
          <div class="ap-chip-pop">
            <div class="ap-chip-pop__title">Publishes to</div>
            <div class="ap-chip-pop__check-col">
              ${destOptions.map(o => `
                <label class="ap-chip-check">
                  <input type="checkbox" id="ap-chip-dest-${o.key}" value="${o.key}" ${(cfg.blog_destinations || settings.blog_destinations || ['website']).includes(o.key) ? 'checked' : ''}>
                  ${escapeHtml(o.label)}
                </label>`).join('')}
            </div>
            <div class="ap-chip-pop__actions">
              <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="close-chip-pop">Cancel</button>
              <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="save-chip-destinations">Save</button>
            </div>
          </div>` : '';
        const platPop = popover === 'platforms' ? `
          <div class="ap-chip-pop">
            <div class="ap-chip-pop__title">Promotes on</div>
            <div class="ap-chip-pop__check-col">
              ${platOptions.map(o => `
                <label class="ap-chip-check">
                  <input type="checkbox" id="ap-chip-plat-${o.key}" value="${o.key}" ${(cfg.social_platforms || settings.social_platforms || []).includes(o.key) ? 'checked' : ''}>
                  ${escapeHtml(o.label)}
                </label>`).join('')}
            </div>
            <div class="ap-chip-pop__hint">Each checked platform gets a promo draft auto-created when the blog publishes.</div>
            <div class="ap-chip-pop__actions">
              <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="close-chip-pop">Cancel</button>
              <button class="ap-btn ap-btn--primary ap-btn--sm" data-action="save-chip-platforms">Save</button>
            </div>
          </div>` : '';

        return `
          <div class="ap-next-line">
            <span class="ap-next-line__icon">→</span>
            Next blog:
            <div class="ap-chip-wrap">
              <button class="ap-next-chip ${popover === 'schedule' ? 'ap-next-chip--open' : ''}" data-action="open-chip-schedule" title="Change publish day &amp; time">${escapeHtml(dayLabel)} at ${h12}:00 ${meridiem}</button>
              ${schedPop}
            </div>
            <span class="ap-next-line__sep">&middot;</span>
            Publishes to
            <div class="ap-chip-wrap">
              <button class="ap-next-chip ${popover === 'destinations' ? 'ap-next-chip--open' : ''}" data-action="open-chip-destinations" title="Change blog destinations">${escapeHtml(destStr)}</button>
              ${destPop}
            </div>
            <span class="ap-next-line__sep">&middot;</span>
            Promotes on
            <div class="ap-chip-wrap">
              <button class="ap-next-chip ${popover === 'platforms' ? 'ap-next-chip--open' : ''}" data-action="open-chip-platforms" title="Change social platforms">${escapeHtml(platStr)}</button>
              ${platPop}
            </div>
          </div>
        `;
    }

    _renderReflectionCard() {
        const r = this.state.data && this.state.data.lastReflection;
        if (!r) {
            return `
              <section class="ap-card ap-card--compact">
                <div class="ap-info"><strong>Monthly reflection.</strong> On the 1st of each month the Brain analyzes what worked + writes fresh rules into your brand memory. Your first reflection shows up here once you've published a full month.</div>
              </section>
            `;
        }
        const periodLabel = r.period_year + '-' + String(r.period_month).padStart(2, '0');
        let lean = [], avoid = [];
        try { lean = JSON.parse(r.themes_to_lean_into_json || '[]'); } catch(_) {}
        try { avoid = JSON.parse(r.themes_to_avoid_json || '[]'); } catch(_) {}
        const isOpen = !!this.state.reflectionOpen;
        let insights = [];
        try { insights = JSON.parse(r.insights_json || '[]'); } catch(_) {}
        return `
          <section class="ap-card ap-reflect-card ${isOpen ? 'ap-reflect-card--open' : ''}">
            <header class="ap-card__head ap-reflect-head" data-action="toggle-reflection" style="cursor: pointer;">
              <div class="ap-card__badge">Last month</div>
              <h3>What the Brain learned</h3>
              <p>${periodLabel} reflection — saved to your brand memory so next month’s topics are sharper.</p>
              <button class="ap-detail-close ap-reflect-toggle" data-action="toggle-reflection" title="${isOpen ? 'Collapse' : 'See insights'}" aria-label="Toggle insights">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform: rotate(${isOpen ? 180 : 0}deg); transition: transform .2s"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
            </header>
            ${lean.length ? `<div class="ap-reflect"><div class="ap-reflect__label">Lean into</div><div class="ap-reflect__chips">${lean.slice(0,5).map(t => `<span class="ap-reflect__chip ap-reflect__chip--good">${escapeHtml(t.theme)}</span>`).join('')}</div></div>` : ''}
            ${avoid.length ? `<div class="ap-reflect"><div class="ap-reflect__label">Avoid</div><div class="ap-reflect__chips">${avoid.slice(0,5).map(t => `<span class="ap-reflect__chip ap-reflect__chip--bad">${escapeHtml(t.theme)}</span>`).join('')}</div></div>` : ''}
            ${isOpen ? `
              <div class="ap-reflect-expanded">
                <div class="ap-reflect-section">
                  <div class="ap-detail-meta__label">Insights saved to your Brand Brain</div>
                  ${insights.length ? `<ul class="ap-reflect-insights">${insights.slice(0, 12).map(ins => {
                      const txt = typeof ins === 'string' ? ins : (ins.insight || ins.text || ins.content || JSON.stringify(ins));
                      return `<li>${escapeHtml(String(txt).slice(0, 300))}</li>`;
                  }).join('')}</ul>` : `<p class="ap-reflect-empty">No specific insights saved this month — the lean/avoid themes above are the summary.</p>`}
                </div>
                <div class="ap-reflect-actions">
                  <a href="#memory?tab=facts" class="ap-btn ap-btn--ghost ap-btn--sm">Open in Brand Brain →</a>
                </div>
              </div>
            ` : ''}
          </section>
        `;
    }

    _renderSettingsModal() {
        const s = this.state.data && this.state.data.settings;
        if (!s || !this.state.settingsOpen) return '';
        const dests = s.blog_destinations || [];
        const platforms = s.social_platforms || [];
        const destCheck = (key, label) => `<label class="ap-check"><input type="checkbox" data-apset="blog_destinations" data-val="${key}" ${dests.includes(key) ? 'checked' : ''}> ${label}</label>`;
        const platCheck = (key, label) => `<label class="ap-check"><input type="checkbox" data-apset="social_platforms" data-val="${key}" ${platforms.includes(key) ? 'checked' : ''}> ${label}</label>`;
        return `
          <div class="ap-modal-overlay" data-action="close-settings">
            <div class="ap-modal">
              <div class="ap-modal__head">
                <h3>Advanced autopilot settings</h3>
                <button class="ap-btn ap-btn--ghost ap-btn--sm" data-action="close-settings">Close</button>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Autonomy</div>
                <div class="ap-field__hint">How much autopilot does without waiting for you.</div>
                <select data-apset="preset" data-testid="ap-preset-select">
                  ${['manual_review', 'assisted', 'autonomous'].map(p => {
                      const current = { cautious: 'manual_review', balanced: 'assisted', full_auto: 'autonomous' }[s.preset] || s.preset;
                      return `<option value="${p}" ${current === p ? 'selected' : ''}>${PRESET_LABELS[p].label} — ${PRESET_LABELS[p].oneLiner}</option>`;
                  }).join('')}
                </select>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Blog destinations</div>
                <div class="ap-field__hint">Every autopilot blog gets published to all checked destinations.</div>
                <div class="ap-check-row">
                  ${destCheck('website', 'Heist-hosted blog (heistbrain.com/blog)')}
                  ${destCheck('wordpress', 'WordPress (your connected site)')}
                </div>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Social platforms for promo cascade</div>
                <div class="ap-field__hint">When a blog publishes, promo drafts are auto-created for every checked platform.</div>
                <div class="ap-check-row">
                  ${platCheck('linkedin', 'LinkedIn')}
                  ${platCheck('instagram', 'Instagram')}
                  ${platCheck('x', 'X / Twitter')}
                  ${platCheck('facebook', 'Facebook')}
                  ${platCheck('tiktok', 'TikTok')}
                  ${platCheck('youtube', 'YouTube')}
                </div>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Publish day + hour</div>
                <div class="ap-field-row">
                  <select data-apset="publish_day">
                    ${['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => `<option value="${d}" ${s.publish_day === d ? 'selected' : ''}>${d.charAt(0).toUpperCase()+d.slice(1)}</option>`).join('')}
                  </select>
                  <select data-apset="publish_hour">
                    ${Array.from({length:24}, (_,h) => `<option value="${h}" ${s.publish_hour === h ? 'selected' : ''}>${h.toString().padStart(2,'0')}:00</option>`).join('')}
                  </select>
                </div>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Approvals</div>
                <label class="ap-check"><input type="checkbox" data-apset="blog_approval_required" ${s.blog_approval_required ? 'checked' : ''}> Require my approval before a blog publishes</label>
                <label class="ap-check"><input type="checkbox" data-apset="social_approval_required" ${s.social_approval_required ? 'checked' : ''}> Require my approval before social promos fire</label>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Topic veto window</div>
                <div class="ap-field__hint">How many hours before publish you're notified to veto or swap the picked topic.</div>
                <div class="ap-field-row">
                  <input type="number" min="1" max="168" step="1" class="ap-modal__input" style="width:120px" data-apset="topic_veto_hours_before" data-testid="ap-veto-hours" value="${s.topic_veto_hours_before != null ? Number(s.topic_veto_hours_before) : 14}">
                  <span style="align-self:center;color:var(--text-muted);font-size:13px">hours before publish</span>
                </div>
                <label class="ap-check"><input type="checkbox" data-apset="auto_default_on_no_response" ${s.auto_default_on_no_response ? 'checked' : ''}> If I don't respond in time, auto-pick the top topic and continue</label>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">AI media</div>
                <label class="ap-check" data-testid="ap-include-ai-images-label">
                  <input type="checkbox" data-apset="include_ai_images" data-testid="ap-include-ai-images" ${s.include_ai_images ? 'checked' : ''}>
                  Generate an AI featured image for each blog post
                </label>
                <small style="display:block;color:var(--text-muted);font-size:11px;margin-top:4px;margin-left:24px">
                  Uses your OpenAI BYOK key. ~$0.04 per post on DALL·E 3. Falls back gracefully if your key isn't configured.
                </small>
              </div>

              <div class="ap-field">
                <div class="ap-field__label">Notifications</div>
                <label class="ap-check"><input type="checkbox" data-apset="notify_email" ${s.notify_email ? 'checked' : ''}> Email me when it's time to pick a topic</label>
                <label class="ap-check"><input type="checkbox" data-apset="notify_in_app" ${s.notify_in_app ? 'checked' : ''}> Send in-app notifications</label>
              </div>

              <div class="ap-modal__actions">
                <button class="ap-btn ap-btn--primary" data-action="save-settings" data-testid="ap-save-settings">Save changes</button>
              </div>
            </div>
          </div>
        `;
    }

    _onAction(action, data) {
        switch (action) {
            case 'enable': return this._enable(data.preset || 'assisted');
            case 'inspect-topic': return this._inspectTopic(data.topicId);
            case 'add-src': return this._addCustomSource();
            case 'toggle-src': return this._toggleSource(data.srcId, data.srcActive);
            case 'delete-src': return this._deleteSource(data.srcId);
            case 'pause': return this._pause();
            case 'toggle-pause-menu': {
                this.state.pauseMenuOpen = !this.state.pauseMenuOpen;
                this._render();
                return;
            }
            case 'pause-duration': {
                const days = Number(data.days) || 7;
                this.state.pauseMenuOpen = false;
                return this._pauseFor(days);
            }
            case 'resume': return this._resume();
            case 'pick-topic': return this._pickTopic(data.topicId);
            case 'toggle-sources-help': {
                this.state.sourcesHelpOpen = !this.state.sourcesHelpOpen;
                this._render();
                return;
            }
            case 'toggle-reflection': {
                this.state.reflectionOpen = !this.state.reflectionOpen;
                this._render();
                return;
            }
            case 'dismiss-welcome': {
                try { localStorage.setItem('autopilot.welcome_seen', '1'); } catch(_) {}
                this._render();
                return;
            }
            case 'open-chip-schedule': {
                this.state.chipPopover = this.state.chipPopover === 'schedule' ? null : 'schedule';
                this._render();
                return;
            }
            case 'open-chip-destinations': {
                this.state.chipPopover = this.state.chipPopover === 'destinations' ? null : 'destinations';
                this._render();
                return;
            }
            case 'open-chip-platforms': {
                this.state.chipPopover = this.state.chipPopover === 'platforms' ? null : 'platforms';
                this._render();
                return;
            }
            case 'close-chip-pop': {
                this.state.chipPopover = null;
                this._render();
                return;
            }
            case 'save-chip-schedule': {
                const day = document.getElementById('ap-chip-day')?.value;
                const hour = Number(document.getElementById('ap-chip-hour')?.value);
                return this._saveChipSettings({ publish_day: day, publish_hour: hour });
            }
            case 'save-chip-destinations': {
                const all = ['website','wordpress'];
                const selected = all.filter(k => document.getElementById('ap-chip-dest-' + k)?.checked);
                return this._saveChipSettings({ blog_destinations: selected });
            }
            case 'save-chip-platforms': {
                const all = ['linkedin','instagram','x','facebook','tiktok','youtube'];
                const selected = all.filter(k => document.getElementById('ap-chip-plat-' + k)?.checked);
                return this._saveChipSettings({ social_platforms: selected });
            }
            case 'dismiss-edit-banner': {
                this.state.showEditedBanner = false;
                this.state.editedTopicId = null;
                this._render();
                return;
            }
            case 'chat-about-topic': return this._chatAboutTopic(data.topicId, data.topicTitle);
            case 'select-stream': return this._selectStream(data.streamId);
            case 'open-new-stream': return this._openNewStream();
            case 'close-new-stream': return this._closeNewStream();
            case 'change-stream-type': {
                const typeEl = document.getElementById('ap-new-stream-type');
                if (typeEl) {
                    this.state.newStreamType = typeEl.value;
                    this._render();
                }
                return;
            }
            case 'submit-new-stream': {
                const nameEl = document.getElementById('ap-new-stream-name');
                const typeEl = document.getElementById('ap-new-stream-type');
                const name = (nameEl && nameEl.value || '').trim() || 'Untitled stream';
                const type = (typeEl && typeEl.value) || 'blog_cycle';
                const config = {};
                if (type === 'campaign') {
                    config.brief = (document.getElementById('ap-wiz-brief')?.value || '').trim();
                    config.platforms = (document.getElementById('ap-wiz-platforms')?.value || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
                    config.posts_per_platform = Number(document.getElementById('ap-wiz-ppp')?.value) || 5;
                    const end = document.getElementById('ap-wiz-end')?.value;
                    if (end) config.end_date = new Date(end).toISOString();
                    if (!config.brief) { toast('error', 'Campaign brief is required'); return; }
                } else if (type === 'social_only') {
                    config.topic_bank = (document.getElementById('ap-wiz-bank')?.value || '').split('\n').map(x => x.trim()).filter(Boolean);
                    config.platforms = (document.getElementById('ap-wiz-platforms')?.value || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
                    config.cadence_per_week = Number(document.getElementById('ap-wiz-cadence')?.value) || 3;
                    if (!config.topic_bank.length) { toast('error', 'Add at least one topic to the bank'); return; }
                } else if (type === 'report') {
                    config.cadence = document.getElementById('ap-wiz-cadence-sel')?.value || 'monthly';
                    config.deliver_email = !!document.getElementById('ap-wiz-email')?.checked;
                }
                return this._createStream(name, type, config);
            }
            case 'pause-stream': return this._pauseStream(data.streamId);
            case 'resume-stream': return this._resumeStream(data.streamId);
            case 'delete-stream': return this._deleteStream(data.streamId);
            case 'delete-topic': return this._deleteTopic(data.topicId);
            case 'toggle-topic-picker': {
                this.state.topicPickerOpenId = this.state.topicPickerOpenId === data.topicId ? null : data.topicId;
                this._render();
                return;
            }
            case 'save-topic-schedule': {
                const input = document.getElementById('ap-topic-date-' + data.topicId);
                const val = input && input.value;
                if (!val) { toast('error', 'Pick a date first'); return; }
                return this._saveTopicSchedule(data.topicId, val);
            }
            case 'clear-topic-schedule': {
                return this._saveTopicSchedule(data.topicId, null);
            }
            case 'seed-now': return this._seedNow();
            case 'send-veto-now': return this._sendVetoNow();
            case 'open-settings':
                this.state.settingsOpen = true;
                this._render();
                return;
            case 'close-settings':
                this.state.settingsOpen = false;
                this._render();
                return;
            case 'save-settings':
                return this._saveSettings();
            case 'open-chat':
                try { sessionStorage.setItem('brain.activeTab', 'chat'); } catch(_) {}
                window.location.hash = '#memory';
                return;
        }
    }
}

function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function renderStyles() {
    return `<style>
      .ap-page { max-width: 960px; margin: 0 auto; padding: 16px 24px 24px; display: flex; flex-direction: column; gap: 20px; }
      .ap-scope .page-shell { padding: 0 var(--page-pad-x, 28px); width: 100%; }
      .ap-scope .page-header { justify-content: flex-start; }
      .ap-loading { padding: 40px; text-align: center; color: var(--text-muted); }
      .ap-spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid var(--border); border-top-color: var(--text); border-radius: 50%; animation: ap-spin 0.7s linear infinite; margin-right: 8px; vertical-align: -2px; }
      .ap-spinner--sm { width: 10px; height: 10px; border-width: 1.5px; margin-right: 0; vertical-align: middle; }
      @keyframes ap-spin { to { transform: rotate(360deg); } }
      .ap-q-del { width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center; margin-left: 8px; border: none; background: transparent; color: var(--text-muted); font-size: 18px; line-height: 1; border-radius: 6px; cursor: pointer; transition: background 0.12s, color 0.12s; }
      .ap-q-del:hover { background: rgba(239,68,68,0.12); color: var(--danger-text); }
      .ap-q-del:disabled { cursor: wait; opacity: 0.5; }
      .ap-q-list--cards { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
      .ap-q-list--cards .ap-q-item--card, .ap-q-list .ap-q-item--card { display: block; padding: 0; background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 12px; overflow: visible; }
      .ap-q-item--card.ap-q-item--expanded { border-color: rgba(139,92,246,0.35); }
      .ap-q-head { display: grid; grid-template-columns: 32px minmax(0, 1fr) 54px auto 24px; align-items: center; gap: 14px; padding: 12px 16px; cursor: pointer; transition: background 0.12s; }
      .ap-q-head:hover { background: var(--surface-3); }
      .ap-q-rank { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); min-width: 28px; }
      .ap-q-titlewrap { min-width: 0; }
      .ap-q-title-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .ap-q-status { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; padding: 2px 7px; border-radius: 999px; color: var(--text-muted); background: var(--surface-2); }
      .ap-q-status--chosen { color: var(--warning-text); background: rgba(245,158,11,0.15); }
      .ap-q-status--active { color: var(--success-text); background: rgba(34,197,94,0.15); }
      .ap-q-title { font-size: 14px; font-weight: 600; color: var(--text); }
      .ap-q-hook { margin-top: 4px; font-size: 12px; color: var(--text-muted); line-height: 1.5; }
      .ap-q-score { font-size: 13px; font-weight: 700; color: var(--primary-text); padding: 4px 0; border-radius: 999px; background: rgba(139,92,246,0.12); text-align: center; min-width: 48px; }
      .ap-q-actions { display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; }
      .ap-q-actions .ap-q-del { margin-left: 0; }
      .ap-q-sched { position: relative; }
      .ap-q-ico { background: transparent; border: 1px solid var(--border); color: var(--text-muted); padding: 5px 8px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; font-size: 11px; }
      .ap-q-ico:hover { color: var(--text); border-color: rgba(139,92,246,0.5); background: rgba(139,92,246,0.08); }
      .ap-q-sched-label { font-weight: 600; letter-spacing: 0.02em; }
      .ap-q-picker { position: absolute; right: 0; top: calc(100% + 6px); background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; padding: 12px; min-width: 230px; z-index: 20; box-shadow: 0 18px 48px rgba(0,0,0,0.45); }
      .ap-q-picker label { display: flex; flex-direction: column; gap: 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); font-weight: 600; }
      .ap-q-picker input[type="date"] { padding: 8px 10px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--text); font-size: 13px; text-transform: none; letter-spacing: 0; }
      .ap-q-picker-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 10px; }
      .ap-q-chev { color: var(--text-muted); font-size: 18px; }
      .ap-q-inline-detail { padding: 0 14px 14px; }
      .ap-detail-panel { padding: 20px 22px 18px; background: linear-gradient(180deg, rgba(139,92,246,0.06), rgba(139,92,246,0.02)); border-top: 1px solid rgba(139,92,246,0.2); }
      .ap-detail-panel__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
      .ap-detail-panel__eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--primary-text); }
      .ap-detail-panel__title { margin: 2px 0 0; font-size: 17px; font-weight: 700; color: var(--text); }
      .ap-detail-close { background: transparent; border: none; color: var(--text-muted); width: 32px; height: 32px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: background 0.12s, color 0.12s; }
      .ap-detail-close:hover { background: var(--surface-2); color: var(--text); }
      .ap-detail-hook { margin: 0 0 18px; padding: 12px 14px; background: var(--surface-2); border-left: 3px solid rgba(139,92,246,0.5); border-radius: 8px; font-size: 13px; line-height: 1.6; color: var(--text-muted); font-style: italic; }
      .ap-detail-grid { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 22px; }
      @media (max-width: 720px) { .ap-detail-grid { grid-template-columns: 1fr; } }
      .ap-detail-meta { margin-bottom: 16px; }
      .ap-detail-meta__label { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
      .ap-detail-meta__val { font-size: 13px; color: var(--text); line-height: 1.6; }
      .ap-detail-meta__chips { display: flex; flex-wrap: wrap; gap: 5px; }
      .ap-detail-chip { font-size: 11px; padding: 3px 9px; border-radius: 999px; background: var(--surface-2); border: 1px solid var(--border-subtle); color: var(--text-muted); }
      .ap-detail-actions { display: flex; justify-content: flex-end; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-subtle); }
      .ap-q-inline-detail { padding: 0; margin: 0; }
      .ap-reflect-card { position: relative; }
      .ap-reflect-head { position: relative; padding-right: 48px; }
      .ap-reflect-toggle { position: absolute; right: 12px; top: 16px; }
      .ap-reflect-expanded { border-top: 1px solid var(--border-subtle); padding: 16px 0 4px; margin-top: 14px; }
      .ap-reflect-section { margin-bottom: 12px; }
      .ap-reflect-insights { list-style: none; padding: 0; margin: 8px 0 0; display: flex; flex-direction: column; gap: 8px; }
      .ap-reflect-insights li { padding: 10px 12px; background: var(--surface-2); border-left: 3px solid rgba(139,92,246,0.4); border-radius: 6px; font-size: 13px; line-height: 1.5; color: var(--text); }
      .ap-reflect-empty { font-size: 13px; color: var(--text-muted); font-style: italic; margin: 8px 0 0; }
      .ap-reflect-actions { display: flex; justify-content: flex-end; padding-top: 8px; }
      .ap-src-help-btn { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; margin-left: 8px; padding: 0; background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.25); border-radius: 50%; color: var(--primary-text); cursor: pointer; vertical-align: middle; transition: background 0.12s, border-color 0.12s; }
      .ap-src-help-btn:hover { background: rgba(139,92,246,0.2); border-color: rgba(139,92,246,0.5); color: var(--primary-text); }
      .ap-src-help-pop { margin-top: 10px; padding: 14px 16px; background: rgba(139,92,246,0.06); border: 1px solid rgba(139,92,246,0.22); border-radius: 12px; }
      .ap-src-help-pop__title { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--primary-text); margin-bottom: 4px; }
      .ap-src-help-pop__intro { font-size: 12px; color: var(--text-muted); margin: 0 0 10px; line-height: 1.5; }
      .ap-src-help-list { list-style: none; padding: 0; margin: 0 0 10px; display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 6px; }
      .ap-src-help-list li { font-size: 12px; padding: 6px 10px; background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 8px; color: var(--text); }
      .ap-src-help-type { font-size: 9px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--primary-text); margin-right: 6px; }
      .ap-src-help-pop__empty { font-size: 12px; color: var(--text-muted); font-style: italic; }
      .ap-src-help-pop__foot { display: flex; justify-content: flex-end; }
      .ap-welcome-card { background: linear-gradient(135deg, rgba(139,92,246,0.15), rgba(99,102,241,0.05)); border: 1px solid rgba(139,92,246,0.35); border-radius: 14px; padding: 18px 20px; margin-bottom: 14px; }
      .ap-welcome-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 10px; }
      .ap-welcome-icon { font-size: 26px; line-height: 1; }
      .ap-welcome-head h3 { margin: 0 0 2px; font-size: 17px; color: var(--text); font-weight: 700; }
      .ap-welcome-head p { margin: 0; color: var(--text-muted); font-size: 13px; }
      .ap-welcome-dismiss { margin-left: auto; background: transparent; border: none; color: var(--text-muted); width: 32px; height: 32px; border-radius: 8px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: background 0.12s, color 0.12s; }
      .ap-welcome-dismiss:hover { color: var(--text); background: var(--surface-3); }
      .ap-welcome-steps { list-style: decimal; padding-left: 24px; margin: 8px 0 14px; color: var(--text-muted); font-size: 13px; line-height: 1.7; }
      .ap-welcome-steps li { margin-bottom: 4px; }
      .ap-welcome-steps strong { color: var(--text); font-weight: 700; }
      .ap-welcome-steps em { color: var(--primary-text); font-style: normal; }
      .ap-welcome-actions { display: flex; justify-content: flex-end; }
      .ap-edit-banner { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: linear-gradient(90deg, rgba(34,197,94,0.12), rgba(34,197,94,0.02)); border: 1px solid rgba(34,197,94,0.3); border-radius: 12px; margin-bottom: 14px; color: var(--success-text); font-size: 13px; }
      .ap-edit-banner__dot { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(34,197,94,0.25); color: var(--success-text); font-weight: 700; }
      .ap-edit-banner button { margin-left: auto; }
      .ap-q-item--flash { animation: ap-flash 2.4s ease-out; }
      @keyframes ap-flash {
        0% { background: rgba(34,197,94,0.25); border-color: rgba(34,197,94,0.7); }
        60% { background: rgba(34,197,94,0.15); border-color: rgba(34,197,94,0.45); }
        100% { background: var(--surface-2); border-color: var(--border-subtle); }
      }
      .ap-str-list-card .ap-card__head { padding-bottom: 10px; }
      .ap-str-head-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; width: 100%; }
      .ap-str-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; margin-top: 6px; }
      .ap-str-card { padding: 14px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface-2); cursor: pointer; transition: border 0.12s, background 0.12s; }
      .ap-str-card:hover { background: var(--surface-3); }
      .ap-str-card--active { border-color: var(--primary); background: rgba(139,92,246,0.08); }
      .ap-str-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
      .ap-str-name-wrap { min-width: 0; }
      .ap-str-name { font-weight: 600; font-size: 15px; color: var(--text); }
      .ap-str-default { margin-left: 6px; font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-muted); background: var(--surface-2); padding: 2px 6px; border-radius: 4px; vertical-align: middle; }
      .ap-str-meta { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
      .ap-str-type { text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; color: var(--primary-text); }
      .ap-str-state { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 999px; white-space: nowrap; }
      .ap-str-state--on { color: var(--success-text); background: rgba(34,197,94,0.12); }
      .ap-str-state--paused { color: var(--warning-text); background: rgba(245,158,11,0.12); }
      .ap-str-state--off { color: var(--text-muted); background: var(--surface-2); }
      .ap-str-ctls { display: flex; gap: 6px; margin-top: 10px; flex-wrap: wrap; }
      .ap-str-ctl { font-size: 11px; font-weight: 600; padding: 4px 10px; border: 1px solid var(--border); background: transparent; color: var(--text-muted); border-radius: 6px; cursor: pointer; transition: color 0.12s, border-color 0.12s; }
      .ap-str-ctl:hover { color: var(--text); border-color: rgba(139,92,246,0.45); }
      .ap-str-ctl--danger:hover { color: var(--danger-text); border-color: rgba(239,68,68,0.4); }
      .ap-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 1000; }
      .ap-modal { background: var(--surface-1); border: 1px solid var(--border); border-radius: 14px; padding: 24px; width: min(440px, 92vw); max-height: 86vh; overflow-y: auto; box-shadow: 0 24px 80px rgba(0,0,0,0.5); cursor: default; }
      .ap-modal h4 { margin: 0 0 6px; font-size: 20px; font-weight: 700; }
      .ap-modal__sub { color: var(--text-muted); font-size: 13px; margin: 0 0 16px; }
      .ap-modal__label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 10px; }
      .ap-modal__input { display: block; width: 100%; margin-top: 6px; padding: 10px 12px; font-size: 14px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--text); text-transform: none; letter-spacing: normal; }
      .ap-modal__input:focus { outline: none; border-color: var(--primary); }
      .ap-modal__actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }
      .ap-modal__head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
      .ap-modal__head h3 { margin: 0; font-size: 19px; font-weight: 700; }
      .ap-field { margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--border-subtle); }
      .ap-field:last-of-type { border-bottom: none; }
      .ap-field__label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 4px; }
      .ap-field__hint { font-size: 12px; color: var(--text-muted); margin-bottom: 10px; line-height: 1.5; }
      .ap-check-row { display: flex; flex-direction: column; gap: 6px; }
      .ap-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text); cursor: pointer; padding: 6px 8px; border-radius: 6px; transition: background 0.1s; }
      .ap-check:hover { background: rgba(139,92,246,0.08); }
      .ap-check input[type="checkbox"] { margin: 0; }
      .ap-field-row { display: flex; gap: 10px; }
      .ap-field-row select { flex: 1; padding: 9px 12px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--text); font-size: 13px; }

      .ap-modal--wizard { width: min(520px, 92vw); }
      .ap-wiz-form { margin-top: 4px; }
      .ap-wiz-row { display: flex; gap: 12px; }
      .ap-wiz-hint { font-size: 12px; color: var(--text-muted); margin: 8px 0 0; line-height: 1.5; }
      .ap-det-body { padding: 4px 0; }
      .ap-det-row { display: flex; justify-content: space-between; gap: 16px; padding: 10px 0; border-bottom: 1px solid var(--border-subtle); font-size: 13px; }
      .ap-det-row span:first-child { color: var(--text-muted); flex: 0 0 140px; }
      .ap-det-row span:last-child { color: var(--text); text-align: right; word-break: break-word; }
      .ap-det-row:last-child { border-bottom: none; }
      .ap-det-pending span:last-child { color: var(--warning-text); font-style: italic; }
      .ap-det-bar { height: 6px; background: var(--surface-2); border-radius: 999px; overflow: hidden; margin: 4px 0 10px; }
      .ap-det-bar__fill { height: 100%; background: linear-gradient(90deg,#8B5CF6,#a78bfa); transition: width 0.3s; }
      .ap-det-list { list-style: none; padding: 0; margin: 0 0 6px; display: flex; flex-wrap: wrap; gap: 6px; }
      .ap-det-list li { font-size: 11px; padding: 3px 8px; background: rgba(139,92,246,0.1); color: var(--primary-text); border-radius: 999px; }

      /* Ribbon */
      .ap-ribbon {
        display: flex; align-items: center; gap: 14px;
        padding: 14px 20px; border-radius: 12px;
        background: var(--surface-1); border: 1px solid var(--border-subtle);
      }
      .ap-ribbon--on { border-color: rgba(16,185,129,0.35); background: linear-gradient(135deg, rgba(16,185,129,0.06), rgba(16,185,129,0.01)); }
      .ap-ribbon--paused { border-color: rgba(245,158,11,0.35); }
      .ap-ribbon--off { border-color: var(--border-subtle); }
      .ap-dot-lg { width: 10px; height: 10px; border-radius: 50%; background: var(--text-muted); flex-shrink: 0; }
      .ap-ribbon--on .ap-dot-lg { background: #10B981; box-shadow: 0 0 10px rgba(16,185,129,0.6); }
      .ap-ribbon--paused .ap-dot-lg { background: #F59E0B; }
      .ap-ribbon__label { font-family: var(--font-mono); font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text); }
      .ap-ribbon__actions { margin-left: auto; display: flex; gap: 8px; }
      .ap-ribbon__actions .ap-btn { white-space: nowrap; }
      .ap-ribbon { flex-wrap: wrap; row-gap: 10px; }

      /* Card */
      .ap-card {
        position: relative; overflow: hidden;
        background: var(--surface-1);
        border: 1px solid var(--border-subtle);
        border-radius: 12px;
        padding: 22px 22px 22px 28px;
      }
      .ap-card--primary { border-color: rgba(245,158,11,0.35); box-shadow: 0 0 0 1px rgba(245,158,11,0.12) inset; }
      .ap-card--compact { padding: 12px 16px; }
      .ap-card__rail { position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--rail, var(--gradient-primary)); }
      .ap-card__head { margin-bottom: 16px; }
      .ap-card__head h3 { margin: 8px 0 6px; font-family: var(--font-display); font-size: 22px; color: var(--text); line-height: 1.15; }
      .ap-card__head p { margin: 0; color: var(--text-2); font-size: 14px; line-height: 1.5; }
      .ap-card__badge { display: inline-block; font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text-2); padding: 3px 8px; border-radius: 999px; background: var(--surface-2); border: 1px solid var(--border-subtle); }

      /* Buttons */
      .ap-actions { display: flex; gap: 10px; flex-wrap: wrap; }
      .ap-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; text-decoration: none; border: 1px solid var(--border); color: var(--text); background: var(--surface-2); transition: all .18s ease; }
      .ap-btn:hover { background: var(--surface-3); border-color: var(--border-strong); }
      .ap-btn--primary { color: #fff; background: linear-gradient(180deg, #F5B841 0%, #E09D28 55%, #C78317 100%); border: 1px solid rgba(245,158,11,0.55); box-shadow: 0 2px 6px rgba(217,119,6,0.25); }
      .ap-btn--ghost { background: transparent; border-color: var(--border-subtle); color: var(--text-2); font-weight: 500; }
      .ap-btn--ghost:hover { background: var(--surface-2); color: var(--text); }
      .ap-btn--sm { padding: 6px 12px; font-size: 12px; }

      /* Presets */
      .ap-presets { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      @media (max-width: 700px) { .ap-presets { grid-template-columns: 1fr; } }
      .ap-preset { position: relative; text-align: left; cursor: pointer; padding: 18px; border-radius: 12px; background: var(--surface-2); border: 1px solid var(--border-subtle); color: var(--text); font-family: inherit; transition: all .18s ease; }
      .ap-preset:hover { border-color: rgba(245,158,11,0.45); background: linear-gradient(135deg, rgba(245,158,11,0.08), rgba(217,119,6,0.02)); }
      .ap-preset__rec { position: absolute; top: 10px; right: 10px; font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--warning-text); padding: 2px 7px; border-radius: 999px; background: rgba(245,158,11,0.15); border: 1px solid rgba(245,158,11,0.35); }
      .ap-preset__label { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
      .ap-preset__desc { font-size: 12.5px; color: var(--text-2); line-height: 1.4; }

      /* Veto list */
      .ap-veto-list { display: flex; flex-direction: column; gap: 10px; }
      .ap-veto-item { display: flex; gap: 12px; align-items: center; padding: 12px; border-radius: 10px; background: var(--surface-2); border: 1px solid var(--border-subtle); }
      .ap-veto-rank { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); min-width: 24px; }
      .ap-veto-body { flex: 1; min-width: 0; }
      .ap-veto-title { font-weight: 600; color: var(--text); font-size: 14px; line-height: 1.35; }
      .ap-veto-hook { color: var(--text-2); font-size: 12.5px; margin-top: 4px; line-height: 1.4; }
      .ap-veto-score { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-muted); margin-top: 6px; letter-spacing: 0.08em; }

      /* Topic queue */
      .ap-q-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
      .ap-q-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 8px; }
      .ap-q-status { font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); min-width: 60px; }
      .ap-q-status--active { color: var(--warning-text); }
      .ap-q-title { flex: 1; font-size: 13.5px; color: var(--text); }
      .ap-q-score { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); padding: 2px 7px; border-radius: 999px; background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.2); }

      /* Cascade preview strip */
      .ap-cascade-preview { margin-top: 14px; padding: 10px 14px; border-radius: 8px; background: rgba(16,185,129,0.06); border: 1px solid rgba(16,185,129,0.2); font-size: 12.5px; color: var(--text-2); line-height: 1.5; }
      .ap-cascade-preview strong { color: var(--success-text); font-family: var(--font-mono); font-size: 11px; letter-spacing: 0.05em; }

      /* Preset explainer + score modal + queue clickability */
      .ap-ribbon__explain { font-size: 12px; color: var(--text-muted); margin-left: 12px; }
      .ap-next-line { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 14px 18px; margin-top: 10px; background: rgba(139,92,246,0.05); border: 1px solid rgba(139,92,246,0.18); border-radius: 12px; font-size: 13px; color: var(--text-muted); line-height: 1.6; }
      .ap-next-line__icon { color: var(--primary-text); font-weight: 700; margin-right: 2px; }
      .ap-next-line__sep { opacity: 0.35; }
      .ap-next-chip { background: var(--surface-2); border: 1px solid var(--border); color: var(--text); padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer; transition: border-color 0.12s, background 0.12s; }
      .ap-next-chip:hover { border-color: rgba(139,92,246,0.55); background: rgba(139,92,246,0.12); }
      .ap-next-chip--open { border-color: rgba(139,92,246,0.9); background: rgba(139,92,246,0.22); }
      .ap-chip-wrap { position: relative; display: inline-flex; }
      .ap-chip-pop { position: absolute; top: calc(100% + 8px); left: 0; background: var(--surface-1); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.55); padding: 14px; min-width: 280px; z-index: 30; }
      .ap-chip-pop__title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); margin-bottom: 10px; }
      .ap-chip-pop__row { display: flex; gap: 10px; }
      .ap-chip-pop__row label { flex: 1; display: flex; flex-direction: column; gap: 4px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); font-weight: 600; }
      .ap-chip-pop__row select { padding: 8px 10px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 8px; color: var(--text); font-size: 13px; text-transform: none; letter-spacing: 0; }
      .ap-chip-pop__check-col { display: flex; flex-direction: column; gap: 6px; }
      .ap-chip-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text); cursor: pointer; padding: 6px 8px; border-radius: 6px; transition: background 0.1s; }
      .ap-chip-check:hover { background: rgba(139,92,246,0.08); }
      .ap-chip-check input[type="checkbox"] { margin: 0; }
      .ap-chip-pop__hint { font-size: 11px; color: var(--text-muted); margin-top: 8px; line-height: 1.5; }
      .ap-chip-pop__actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 12px; }
      .ap-pause-wrap { position: relative; display: inline-flex; }
      .ap-pause-main { border-top-right-radius: 0; border-bottom-right-radius: 0; border-right: none; }
      .ap-pause-caret { border-top-left-radius: 0; border-bottom-left-radius: 0; padding: 0 8px; min-width: 28px; }
      .ap-pause-menu { position: absolute; top: calc(100% + 6px); right: 0; background: var(--surface-1); border: 1px solid var(--border); border-radius: 10px; box-shadow: 0 18px 48px rgba(0,0,0,0.45); min-width: 200px; z-index: 10; overflow: hidden; }
      .ap-pause-menu__item { display: block; width: 100%; text-align: left; padding: 10px 14px; background: transparent; border: none; color: var(--text); font-size: 13px; cursor: pointer; }
      .ap-pause-menu__item:hover { background: rgba(139,92,246,0.12); }
      .ap-preset-wrap { display: flex; flex-direction: column; gap: 6px; }
      .ap-preset__rules { font-size: 12px; color: var(--text-2); background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 6px; padding: 4px 10px; }
      .ap-preset__rules summary { cursor: pointer; color: var(--text-muted); }
      .ap-preset__rules summary:hover { color: var(--text); }
      .ap-preset__rules ul { margin: 8px 0 4px 0; padding-left: 18px; }
      .ap-preset__rules li { margin: 3px 0; }

      .ap-q-item--clickable { cursor: pointer; transition: background .15s ease; }
      .ap-q-item--clickable:hover { background: var(--surface-3); border-color: var(--border); }
      .ap-q-chev { color: var(--text-muted); font-size: 16px; margin-left: auto; }
      .ap-q-item--expanded .ap-q-chev { transform: rotate(90deg); color: var(--primary); }
      .ap-q-item--expanded { background: var(--surface-3); border-color: rgba(139,92,246,0.3); }
      .ap-q-inline-detail { padding: 16px 14px 14px 42px; background: var(--surface-2); border: 1px solid rgba(139,92,246,0.2); border-top: 0; border-radius: 0 0 8px 8px; margin-top: -8px; animation: ap-q-slide .18s ease-out; }
      @keyframes ap-q-slide { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      .ap-inline-loading { color: var(--text-muted); font-size: 12.5px; padding: 4px 0; }
      .ap-inline-head { margin-bottom: 12px; }
      .ap-inline-actions { margin-top: 14px; display: flex; gap: 8px; }

      .ap-score-modal { max-width: 560px; width: 94vw; padding: 24px; }
      .ap-score-total { font-family: var(--font-display); font-size: 48px; font-weight: 600; color: var(--warning-text); line-height: 1; margin-bottom: 6px; }
      .ap-score-hook { color: var(--text-2); font-size: 13.5px; line-height: 1.5; margin: 8px 0 20px; }
      .ap-score-bars { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
      .ap-score-row { display: grid; grid-template-columns: 140px 1fr 50px; gap: 12px; align-items: center; font-size: 12.5px; }
      .ap-score-row__label { color: var(--text-2); cursor: help; }
      .ap-score-bar { height: 8px; background: var(--surface-2); border-radius: 4px; overflow: hidden; }
      .ap-score-bar__fill { height: 100%; background: linear-gradient(90deg, #8B5CF6, #6D28D9); transition: width .3s ease; }
      .ap-score-bar__fill--neg { background: linear-gradient(90deg, #EF4444, #DC2626); }
      .ap-score-row { grid-template-columns: 140px 1fr 78px !important; }
      .ap-score-row__val { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); text-align: right; }
      .ap-score-total-line { display: flex; justify-content: space-between; align-items: baseline; padding: 10px 0 14px; border-top: 1px dashed var(--border-subtle); margin-top: 8px; font-family: var(--font-mono); font-size: 12px; color: var(--text-2); text-transform: uppercase; letter-spacing: 0.08em; }
      .ap-score-total-val { font-family: var(--font-display); font-size: 20px; color: var(--warning-text); font-weight: 600; letter-spacing: 0; }
      .ap-score-total-max { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); font-weight: normal; }
      /* Custom sources card */
      .ap-src-list { list-style: none; padding: 0; margin: 0 0 16px; display: flex; flex-direction: column; gap: 6px; }
      .ap-src-item { display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 8px; font-size: 13px; }
      .ap-src-item--off { opacity: 0.55; }
      .ap-src-type { font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); min-width: 52px; }
      .ap-src-label { flex: 1; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .ap-src-empty { padding: 18px; text-align: center; color: var(--text-muted); font-size: 13px; background: var(--surface-2); border: 1px dashed var(--border-subtle); border-radius: 8px; }
      .ap-src-add { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
      .ap-src-add .ap-input { flex: 1; min-width: 180px; padding: 8px 12px; font-size: 13px; background: var(--surface-2); border: 1px solid var(--border-subtle); border-radius: 8px; color: var(--text); font-family: inherit; }
      .ap-src-add .ap-input--short { flex: 0 0 140px; }
      .ap-score-sources { font-family: var(--font-mono); font-size: 10.5px; color: var(--text-muted); letter-spacing: 0.05em; padding-top: 12px; border-top: 1px solid var(--border-subtle); margin-bottom: 16px; }
      .ap-source-label { color: var(--text-2); font-weight: 600; margin-right: 4px; }
      .ap-score-sources--all { padding-top: 6px; border-top: 0; opacity: 0.85; }

      /* Info */
      .ap-info { font-size: 13px; color: var(--text-2); line-height: 1.55; }
      .ap-info strong { color: var(--text); }
      .ap-info a { color: var(--primary); text-decoration: none; border-bottom: 1px dashed var(--primary); margin-left: 4px; }
    </style>`;
}
